import {
  doc,
  collection,
  addDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../firebase/config";
import {
  ALL_DIMENSIONS,
  DeviceDimension,
  GENERATOR_DIMENSIONS,
  GENERATOR_IOS_DIMENSIONS,
  ScreenshotTemplate,
  LocalizedScreenshot,
  CreateTemplateDTO,
  GenerateScreenshotDTO,
} from "../types/screenshot";
import {
  GeneratedStoreScreenshot,
  AndroidDeviceFrameId,
  StorePreviewDeviceId,
  STORE_DEVICE_FRAME_PROFILES,
  StoreBackgroundPattern,
  StoreGradientPresetId,
  StoreScreenshotGenerationProgress,
  StoreFontStyle,
  StoreTemplateLayoutId,
  WaveStyleVariant,
  ScreenshotTargetPlatform,
  StoreScreenshotTemplateStyle,
  StoreCompositionLayerKind,
  StoreScreenshotTemplate,
  StoreScreenshotGenerationMode,
  StoreScreenshotTemplateScreenSlot,
  StoredStoreScreenshotAsset,
  StoreScreenshotGenerationVersion,
  StoreScreenshotZipExportJob,
  GooglePlayScreenshotPublishMode,
  GooglePlayScreenshotPublishResponse,
  GooglePlayScreenshotValidationResponse,
} from "../types/screenshot";
import JSZip from "jszip";
import AIService from "./AIService";
import type { App } from "../types/app";
import { buildAppContextLines } from "../utils/appContextPrompt";

type ScreenshotJobRecord = {
  id: string;
  appId: string;
  appName: string;
  templateId: string;
  templateName: string;
  targetPlatform: ScreenshotTargetPlatform;
  locales: string[];
  dimensionLabels: string[];
  sourceCount: number;
  expectedOutputCount: number;
  generatedCount: number;
  savedVersionId?: string;
  savedVersionNumber?: number;
  zipDownloadUrl?: string;
  zipFileName?: string;
  zipCreatedAt?: string;
  status: "queued" | "processing" | "completed" | "failed";
  progress: StoreScreenshotGenerationProgress | null;
  createdAt: string;
  updatedAt: string;
  error?: string;
};

const TEMPLATES_COLLECTION = "screenshotTemplates";
const SCREENSHOTS_COLLECTION = "localizedScreenshots";
const STORE_TEMPLATE_STYLES_COLLECTION = "storeScreenshotTemplateStyles";
const STORE_CUSTOM_TEMPLATES_COLLECTION = "storeScreenshotCustomTemplates";
const STORE_SCREENSHOT_VERSIONS_COLLECTION = "storeScreenshotVersions";
const STORE_SCREENSHOT_ASSETS_COLLECTION = "storeScreenshotAssets";
const STORE_SCREENSHOT_ZIP_EXPORTS_COLLECTION = "storeScreenshotZipExports";
const STORE_SCREENSHOT_JOBS_COLLECTION = "storeScreenshotJobs";
const GOOGLE_PLAY_SCREENSHOT_PUBLISH_JOBS_COLLECTION =
  "googlePlayScreenshotPublishJobs";
const APP_STORE_CONNECT_SCREENSHOT_PUBLISH_JOBS_COLLECTION =
  "appStoreConnectScreenshotPublishJobs";
const PUBLISH_GOOGLE_PLAY_SCREENSHOTS_ENDPOINT =
  process.env.REACT_APP_PUBLISH_GOOGLE_PLAY_SCREENSHOTS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/publishGooglePlayScreenshots";
const VALIDATE_GOOGLE_PLAY_SCREENSHOTS_ENDPOINT =
  process.env.REACT_APP_VALIDATE_GOOGLE_PLAY_SCREENSHOTS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateGooglePlayScreenshots";
const PUBLISH_APP_STORE_CONNECT_SCREENSHOTS_ENDPOINT =
  process.env.REACT_APP_PUBLISH_APP_STORE_CONNECT_SCREENSHOTS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/publishAppStoreConnectScreenshots";
const VALIDATE_APP_STORE_CONNECT_SCREENSHOTS_ENDPOINT =
  process.env.REACT_APP_VALIDATE_APP_STORE_CONNECT_SCREENSHOTS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateAppStoreConnectScreenshots";
const GENERATE_STORE_SCREENSHOTS_ENDPOINT =
  process.env.REACT_APP_GENERATE_STORE_SCREENSHOTS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/generateStoreScreenshots";
const SCREENSHOT_HEADING_MAX_WORDS = 6;
const SCREENSHOT_SUBTITLE_MAX_WORDS = 5;

class ScreenshotService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      const error = new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      ) as Error & { responseData?: unknown };
      error.responseData = responseData;
      throw error;
    }

    return responseData as T;
  }

  private readonly DEFAULT_TEMPLATE_STYLE: StoreScreenshotTemplateStyle = {
    backgroundType: "color",
    backgroundColor: "#F6F8FC",
    backgroundPattern: "dot_grid",
    backgroundGradient: "sunset_blush",
    patternColor: "#667085",
    patternOpacity: 0.42,
    patternScale: 1,
    dotSizeScale: 1,
    waveStyle: "balanced",
    headingColor: "#101828",
    subtitleColor: "#475467",
    headingFontFamily: '"SF Pro Display", "Inter", "Segoe UI", sans-serif',
    subtitleFontFamily: '"SF Pro Text", "Inter", "Segoe UI", sans-serif',
    headingFontStyle: "normal",
    subtitleFontStyle: "normal",
    showSubtitle: true,
    headingWeight: 800,
    subtitleWeight: 500,
    headingSizeRatio: 0.058,
    subtitleSizeRatio: 0.03,
    headingXRatio: 0.5,
    headingYRatio: 0.065,
    subtitleXRatio: 0.5,
    subtitleYRatio: 0.135,
    headingMaxWidthRatio: 0.84,
    subtitleMaxWidthRatio: 0.76,
    headingTextAlign: "center",
    subtitleTextAlign: "center",
    useAbsoluteTextPlacement: false,
    showDeviceFrame: true,
    headingTopRatio: 0.065,
    subtitleOffsetRatio: 0.045,
    frameWidthRatio: 0.74,
    frameHeightRatio: 0.78,
    frameTopRatio: 0.16,
    androidDeviceFrameId: "pixel_9_pro",
    mainImageMask: {
      type: "rounded_rect",
      radiusRatio: 0.08,
      insetRatio: 0,
      waveDepthRatio: 0.1,
    },
    mainImageTransform: {
      xRatio: 0.5,
      yRatio: 0.5,
      scale: 1,
      rotationDeg: 0,
      opacity: 1,
    },
    layerStack: [],
  };

  /**
   * Generate a unique template ID
   */
  private generateTemplateId(): string {
    return `template_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;
  }

  /**
   * Generate a unique screenshot ID
   */
  private generateScreenshotId(): string {
    return `screenshot_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;
  }

  /**
   * Convert Firestore timestamp to Date
   */
  private convertTimestampToDate(timestamp: any): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  }

  /**
   * Create a new screenshot template
   */
  async createTemplate(
    userId: string,
    templateData: CreateTemplateDTO,
  ): Promise<ScreenshotTemplate> {
    try {
      const templateId = this.generateTemplateId();
      const now = serverTimestamp();

      const newTemplate = {
        templateId,
        appId: templateData.appId,
        userId,
        name: templateData.name,
        baseImage: templateData.baseImage,
        textOverlays: templateData.textOverlays,
        background: templateData.background,
        deviceFrame: templateData.deviceFrame,
        dimension: templateData.dimension,
        createdAt: now,
        updatedAt: now,
      };

      await addDoc(collection(db, TEMPLATES_COLLECTION), newTemplate);

      return {
        ...newTemplate,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as ScreenshotTemplate;
    } catch (error) {
      console.error("Error creating template:", error);
      throw new Error("Failed to create screenshot template");
    }
  }

  /**
   * Get all templates for an app
   */
  async getTemplatesByAppId(
    appId: string,
    userId: string,
  ): Promise<ScreenshotTemplate[]> {
    try {
      const templatesRef = collection(db, TEMPLATES_COLLECTION);
      const q = query(
        templatesRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          templateId: data.templateId,
          appId: data.appId,
          userId: data.userId,
          name: data.name,
          baseImage: data.baseImage,
          textOverlays: data.textOverlays,
          background: data.background,
          deviceFrame: data.deviceFrame,
          dimension: data.dimension,
          createdAt: this.convertTimestampToDate(data.createdAt),
          updatedAt: this.convertTimestampToDate(data.updatedAt),
        } as ScreenshotTemplate;
      });
    } catch (error) {
      console.error("Error fetching templates:", error);
      throw new Error("Failed to fetch templates");
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string, userId: string): Promise<void> {
    try {
      const templatesRef = collection(db, TEMPLATES_COLLECTION);
      const q = query(
        templatesRef,
        where("templateId", "==", templateId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Template not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting template:", error);
      throw new Error("Failed to delete template");
    }
  }

  /**
   * Generate localized screenshots using AI
   */
  async generateLocalizedScreenshots(
    userId: string,
    template: ScreenshotTemplate,
    request: GenerateScreenshotDTO,
  ): Promise<LocalizedScreenshot[]> {
    try {
      const screenshots: LocalizedScreenshot[] = [];

      // Simulate AI translation and generation for each locale
      for (const locale of request.targetLocales) {
        // Extract text from overlays
        const textsToTranslate = template.textOverlays.map(
          (overlay) => overlay.text,
        );

        // Translate texts (mock implementation)
        const translatedTexts = await this.translateTexts(
          textsToTranslate,
          locale,
        );

        // Generate screenshot with translated text
        const screenshot = await this.generateScreenshot(
          userId,
          template,
          locale,
          translatedTexts,
        );

        screenshots.push(screenshot);
      }

      return screenshots;
    } catch (error) {
      console.error("Error generating localized screenshots:", error);
      throw new Error("Failed to generate localized screenshots");
    }
  }

  /**
   * Translate texts using AI (mock implementation)
   */
  private async translateTexts(
    texts: string[],
    targetLocale: string,
  ): Promise<string[]> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock translations (in production, use Gemini API)
    const mockTranslations: Record<string, Record<string, string>> = {
      es: {
        "Download Now": "Descargar Ahora",
        "Start Free Trial": "Iniciar Prueba Gratuita",
        "Get Started": "Comenzar",
        Features: "Características",
      },
      fr: {
        "Download Now": "Télécharger Maintenant",
        "Start Free Trial": "Commencer l'Essai Gratuit",
        "Get Started": "Commencer",
        Features: "Fonctionnalités",
      },
      de: {
        "Download Now": "Jetzt Herunterladen",
        "Start Free Trial": "Kostenlose Testversion Starten",
        "Get Started": "Loslegen",
        Features: "Funktionen",
      },
      ja: {
        "Download Now": "今すぐダウンロード",
        "Start Free Trial": "無料トライアルを開始",
        "Get Started": "始める",
        Features: "機能",
      },
      zh: {
        "Download Now": "立即下载",
        "Start Free Trial": "开始免费试用",
        "Get Started": "开始使用",
        Features: "功能特点",
      },
    };

    return texts.map((text) => {
      const translations = mockTranslations[targetLocale];
      return translations?.[text] || text;
    });
  }

  /**
   * Generate a single screenshot with translated text
   */
  private async generateScreenshot(
    userId: string,
    template: ScreenshotTemplate,
    locale: string,
    translatedTexts: string[],
  ): Promise<LocalizedScreenshot> {
    const screenshotId = this.generateScreenshotId();
    const now = new Date();

    // In production, this would:
    // 1. Load the base image
    // 2. Use canvas API to overlay translated text
    // 3. Upload to storage and get URL
    // For now, we'll use a mock URL
    const imageUrl = `https://via.placeholder.com/${template.dimension.width}x${template.dimension.height}?text=${locale}`;

    const screenshot: LocalizedScreenshot = {
      screenshotId,
      templateId: template.templateId,
      appId: template.appId,
      userId,
      locale,
      imageUrl,
      dimension: template.dimension,
      metadata: {
        generatedAt: now,
        aiModel: "mock-gemini-vision",
        translatedTexts,
      },
    };

    // Save to Firestore
    const screenshotData = {
      ...screenshot,
      metadata: {
        ...screenshot.metadata,
        generatedAt: serverTimestamp(),
      },
    };

    await addDoc(collection(db, SCREENSHOTS_COLLECTION), screenshotData);

    return screenshot;
  }

  /**
   * Get all localized screenshots for a template
   */
  async getScreenshotsByTemplateId(
    templateId: string,
    userId: string,
  ): Promise<LocalizedScreenshot[]> {
    try {
      const screenshotsRef = collection(db, SCREENSHOTS_COLLECTION);
      const q = query(
        screenshotsRef,
        where("templateId", "==", templateId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          screenshotId: data.screenshotId,
          templateId: data.templateId,
          appId: data.appId,
          userId: data.userId,
          locale: data.locale,
          imageUrl: data.imageUrl,
          dimension: data.dimension,
          metadata: {
            generatedAt: this.convertTimestampToDate(
              data.metadata?.generatedAt,
            ),
            aiModel: data.metadata?.aiModel,
            translatedTexts: data.metadata?.translatedTexts || [],
          },
        } as LocalizedScreenshot;
      });
    } catch (error) {
      console.error("Error fetching screenshots:", error);
      throw new Error("Failed to fetch screenshots");
    }
  }

  /**
   * Get all screenshots for an app
   */
  async getScreenshotsByAppId(
    appId: string,
    userId: string,
  ): Promise<LocalizedScreenshot[]> {
    try {
      const screenshotsRef = collection(db, SCREENSHOTS_COLLECTION);
      const q = query(
        screenshotsRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          screenshotId: data.screenshotId,
          templateId: data.templateId,
          appId: data.appId,
          userId: data.userId,
          locale: data.locale,
          imageUrl: data.imageUrl,
          dimension: data.dimension,
          metadata: {
            generatedAt: this.convertTimestampToDate(
              data.metadata?.generatedAt,
            ),
            aiModel: data.metadata?.aiModel,
            translatedTexts: data.metadata?.translatedTexts || [],
          },
        } as LocalizedScreenshot;
      });
    } catch (error) {
      console.error("Error fetching screenshots:", error);
      throw new Error("Failed to fetch screenshots");
    }
  }

  /**
   * Delete a screenshot
   */
  async deleteScreenshot(screenshotId: string, userId: string): Promise<void> {
    try {
      const screenshotsRef = collection(db, SCREENSHOTS_COLLECTION);
      const q = query(
        screenshotsRef,
        where("screenshotId", "==", screenshotId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Screenshot not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting screenshot:", error);
      throw new Error("Failed to delete screenshot");
    }
  }

  /**
   * Duplicate a template
   */
  async duplicateTemplate(
    templateId: string,
    userId: string,
    newName: string,
  ): Promise<ScreenshotTemplate> {
    try {
      // Get the original template
      const templatesRef = collection(db, TEMPLATES_COLLECTION);
      const q = query(
        templatesRef,
        where("templateId", "==", templateId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Template not found");
      }

      const originalData = querySnapshot.docs[0].data();

      // Create new template
      const createDTO: CreateTemplateDTO = {
        appId: originalData.appId,
        name: newName,
        baseImage: originalData.baseImage,
        textOverlays: originalData.textOverlays,
        background: originalData.background,
        deviceFrame: originalData.deviceFrame,
        dimension: originalData.dimension,
      };

      return await this.createTemplate(userId, createDTO);
    } catch (error) {
      console.error("Error duplicating template:", error);
      throw new Error("Failed to duplicate template");
    }
  }

  /**
   * Download screenshot as blob
   */
  async downloadScreenshot(imageUrl: string, filename: string): Promise<void> {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading screenshot:", error);
      throw new Error("Failed to download screenshot");
    }
  }

  /**
   * Batch download all screenshots for a template
   */
  async batchDownloadScreenshots(
    screenshots: LocalizedScreenshot[],
    appName: string,
  ): Promise<void> {
    for (const screenshot of screenshots) {
      const filename = `${appName}_${screenshot.locale}_${screenshot.dimension.name}_${screenshot.dimension.width}x${screenshot.dimension.height}.png`;
      await this.downloadScreenshot(screenshot.imageUrl, filename);
      // Add delay to avoid overwhelming the browser
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  async saveStoreTemplateStyle(
    userId: string,
    appId: string,
    templateId: string,
    style: StoreScreenshotTemplateStyle,
  ): Promise<void> {
    const styleDocId = this.buildStyleDocId(userId, appId, templateId);
    await setDoc(
      doc(db, STORE_TEMPLATE_STYLES_COLLECTION, styleDocId),
      {
        userId,
        appId,
        templateId,
        style,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  async createCustomStoreTemplate(
    userId: string,
    appId: string,
    template: Omit<
      StoreScreenshotTemplate,
      "id" | "isCustom" | "appId" | "userId" | "createdAt" | "updatedAt"
    >,
  ): Promise<StoreScreenshotTemplate> {
    const templateId = `custom_tpl_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const payload: StoreScreenshotTemplate = {
      id: templateId,
      layout: template.layout,
      name: template.name,
      description: template.description,
      style: template.style,
      generationMode: template.generationMode || "single_source",
      screenSlots: Array.isArray(template.screenSlots)
        ? template.screenSlots
        : [],
      isCustom: true,
      appId,
      userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await setDoc(doc(db, STORE_CUSTOM_TEMPLATES_COLLECTION, templateId), {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return payload;
  }

  async updateCustomStoreTemplate(
    userId: string,
    templateId: string,
    updates: Partial<
      Pick<
        StoreScreenshotTemplate,
        | "name"
        | "description"
        | "style"
        | "layout"
        | "generationMode"
        | "screenSlots"
      >
    >,
  ): Promise<void> {
    const templateRef = doc(db, STORE_CUSTOM_TEMPLATES_COLLECTION, templateId);
    const snap = await getDoc(templateRef);
    if (!snap.exists()) {
      throw new Error("Custom template not found.");
    }
    const data = snap.data();
    if (data?.userId !== userId) {
      throw new Error("You do not have permission to update this template.");
    }
    await setDoc(
      templateRef,
      {
        ...updates,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  async deleteCustomStoreTemplate(
    userId: string,
    templateId: string,
  ): Promise<void> {
    const templateRef = doc(db, STORE_CUSTOM_TEMPLATES_COLLECTION, templateId);
    const snap = await getDoc(templateRef);
    if (!snap.exists()) return;
    const data = snap.data();
    if (data?.userId !== userId) {
      throw new Error("You do not have permission to delete this template.");
    }
    await deleteDoc(templateRef);
  }

  async getCustomStoreTemplates(
    userId: string,
    appId: string,
  ): Promise<StoreScreenshotTemplate[]> {
    const templatesRef = collection(db, STORE_CUSTOM_TEMPLATES_COLLECTION);
    const q = query(
      templatesRef,
      where("userId", "==", userId),
      where("appId", "==", appId),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((item) => {
      const data = item.data() as StoreScreenshotTemplate;
      return {
        ...data,
        id: item.id,
        layout: (data.layout ||
          "simple_heading_device") as StoreTemplateLayoutId,
        generationMode:
          ((data.generationMode as StoreScreenshotGenerationMode) ||
            "single_source") as StoreScreenshotGenerationMode,
        screenSlots: Array.isArray(data.screenSlots)
          ? (data.screenSlots as StoreScreenshotTemplateScreenSlot[])
          : [],
        isCustom: true,
        appId,
        userId,
      };
    });
  }

  async persistGeneratedScreenshots(params: {
    userId: string;
    appId: string;
    templateId: string;
    templateName: string;
    screenshots: GeneratedStoreScreenshot[];
    onProgress?: (progress: StoreScreenshotGenerationProgress) => void;
  }): Promise<{
    version: StoreScreenshotGenerationVersion;
    assets: StoredStoreScreenshotAsset[];
  }> {
    const { userId, appId, templateId, templateName, screenshots, onProgress } =
      params;
    if (screenshots.length === 0) {
      throw new Error("No screenshots to persist.");
    }

    const versionRecord = await this.createScreenshotGenerationVersion({
      userId,
      appId,
      templateId,
      templateName,
      initialAssetCount: screenshots.length,
    });
    const savedAssets = await this.appendGeneratedScreenshotsToVersion({
      userId,
      appId,
      versionId: versionRecord.versionId,
      versionNumber: versionRecord.versionNumber,
      templateId,
      templateName,
      screenshots,
      totalExpectedAssets: screenshots.length,
      existingAssetCount: 0,
      onProgress,
    });

    return {
      version: versionRecord,
      assets: savedAssets,
    };
  }

  async createScreenshotGenerationVersion(params: {
    userId: string;
    appId: string;
    templateId: string;
    templateName: string;
    initialAssetCount?: number;
    expectedAssetCount?: number;
  }): Promise<StoreScreenshotGenerationVersion> {
    const {
      userId,
      appId,
      templateId,
      templateName,
      initialAssetCount = 0,
      expectedAssetCount = initialAssetCount,
    } = params;
    const existingVersions = await this.getScreenshotVersionsByAppId(
      appId,
      userId,
    );
    const nextVersionNumber =
      existingVersions.length === 0
        ? 1
        : Math.max(...existingVersions.map((item) => item.versionNumber || 0)) +
          1;
    const versionId = `ss_ver_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const versionRecord: StoreScreenshotGenerationVersion = {
      versionId,
      appId,
      userId,
      versionNumber: nextVersionNumber,
      templateId,
      templateName,
      createdAt: new Date().toISOString(),
      status: "processing",
      assetCount: initialAssetCount,
      expectedAssetCount,
    };

    await setDoc(doc(db, STORE_SCREENSHOT_VERSIONS_COLLECTION, versionId), {
      ...versionRecord,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return versionRecord;
  }

  async appendGeneratedScreenshotsToVersion(params: {
    userId: string;
    appId: string;
    versionId: string;
    versionNumber: number;
    templateId: string;
    templateName: string;
    screenshots: GeneratedStoreScreenshot[];
    totalExpectedAssets?: number;
    existingAssetCount?: number;
    onProgress?: (progress: StoreScreenshotGenerationProgress) => void;
  }): Promise<StoredStoreScreenshotAsset[]> {
    const {
      userId,
      appId,
      versionId,
      versionNumber,
      templateId,
      templateName,
      screenshots,
      totalExpectedAssets,
      existingAssetCount = 0,
      onProgress,
    } = params;
    if (screenshots.length === 0) {
      return [];
    }

    const totalAssets =
      totalExpectedAssets || existingAssetCount + screenshots.length;
    const savedAssets: StoredStoreScreenshotAsset[] = [];

    onProgress?.({
      stage: "saving",
      message: `Saving locale screenshots to the content library (${existingAssetCount}/${totalAssets})`,
      completed: existingAssetCount,
      total: totalAssets,
      percent:
        totalAssets > 0
          ? Math.round((existingAssetCount / totalAssets) * 100)
          : 0,
    });

    for (const screenshot of screenshots) {
      const assetId = `ss_asset_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const blob = await this.dataUrlToBlob(screenshot.dataUrl);
      const fileName = this.buildScreenshotFileName(
        screenshot,
        screenshot.appName,
      );
      const path = `apps/${appId}/store-screenshots/${versionId}/${assetId}_${fileName}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, blob, {
        contentType: "image/png",
        customMetadata: {
          userId,
          appId,
          versionId,
          assetId,
          category: "store-screenshots",
        },
      });
      const downloadUrl = await getDownloadURL(fileRef);

      const asset: StoredStoreScreenshotAsset = {
        assetId,
        appId,
        userId,
        versionId,
        versionNumber,
        templateId,
        templateName,
        platform: screenshot.platform,
        locale: screenshot.locale,
        sourceName: screenshot.sourceName,
        heading: screenshot.heading,
        subtitle: screenshot.subtitle,
        dimension: screenshot.dimension,
        downloadUrl,
        storagePath: path,
        createdAt: new Date().toISOString(),
      };
      await setDoc(doc(db, STORE_SCREENSHOT_ASSETS_COLLECTION, assetId), {
        ...asset,
        createdAt: serverTimestamp(),
      });
      savedAssets.push(asset);
      const completedCount = existingAssetCount + savedAssets.length;
      await updateDoc(
        doc(db, STORE_SCREENSHOT_VERSIONS_COLLECTION, versionId),
        {
          assetCount: completedCount,
          updatedAt: serverTimestamp(),
        },
      );
      onProgress?.({
        stage: "saving",
        message: `Saving locale screenshots to the content library (${completedCount}/${totalAssets})`,
        completed: completedCount,
        total: totalAssets,
        percent:
          totalAssets > 0
            ? Math.round((completedCount / totalAssets) * 100)
            : 100,
      });
    }

    return savedAssets;
  }

  async finalizeScreenshotGenerationVersion(params: {
    versionId: string;
    status: "completed" | "failed" | "partial";
    assetCount?: number;
    expectedAssetCount?: number;
  }): Promise<void> {
    const payload: {
      status: "completed" | "failed" | "partial";
      updatedAt: ReturnType<typeof serverTimestamp>;
      assetCount?: number;
      expectedAssetCount?: number;
    } = {
      status: params.status,
      updatedAt: serverTimestamp(),
    };
    if (typeof params.assetCount === "number") {
      payload.assetCount = params.assetCount;
    }
    if (typeof params.expectedAssetCount === "number") {
      payload.expectedAssetCount = params.expectedAssetCount;
    }

    await updateDoc(
      doc(db, STORE_SCREENSHOT_VERSIONS_COLLECTION, params.versionId),
      payload,
    );
  }

  async getScreenshotVersionsByAppId(
    appId: string,
    userId: string,
  ): Promise<StoreScreenshotGenerationVersion[]> {
    const versionsRef = collection(db, STORE_SCREENSHOT_VERSIONS_COLLECTION);
    const q = query(
      versionsRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((item) => {
      const data = item.data();
      return {
        versionId: data.versionId || item.id,
        appId: data.appId,
        userId: data.userId,
        versionNumber: Number(data.versionNumber || 1),
        templateId: data.templateId || "",
        templateName: data.templateName || "",
        createdAt: this.convertTimestampToDate(data.createdAt).toISOString(),
        status: data.status || "completed",
        assetCount: Number(data.assetCount || 0),
        expectedAssetCount: Number(
          data.expectedAssetCount || data.assetCount || 0,
        ),
        zipStoragePath: data.zipStoragePath || "",
        zipDownloadUrl: data.zipDownloadUrl || "",
        zipFileName: data.zipFileName || "",
        zipCreatedAt: data.zipCreatedAt
          ? this.convertTimestampToDate(data.zipCreatedAt).toISOString()
          : "",
      };
    });
  }

  async getStoredScreenshotsByAppId(
    appId: string,
    userId: string,
  ): Promise<StoredStoreScreenshotAsset[]> {
    const assetsRef = collection(db, STORE_SCREENSHOT_ASSETS_COLLECTION);
    const q = query(
      assetsRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((item) => {
      const data = item.data();
      return {
        assetId: data.assetId || item.id,
        appId: data.appId,
        userId: data.userId,
        versionId: data.versionId,
        versionNumber: Number(data.versionNumber || 1),
        templateId: data.templateId || "",
        templateName: data.templateName || "",
        platform: data.platform,
        locale: data.locale,
        sourceName: data.sourceName || "",
        heading: data.heading || "",
        subtitle: data.subtitle || "",
        dimension: data.dimension,
        downloadUrl: data.downloadUrl,
        storagePath: data.storagePath,
        createdAt: this.convertTimestampToDate(data.createdAt).toISOString(),
      } as StoredStoreScreenshotAsset;
    });
  }

  async deleteStoredScreenshotAsset(
    userId: string,
    assetId: string,
  ): Promise<void> {
    const assetRef = doc(db, STORE_SCREENSHOT_ASSETS_COLLECTION, assetId);
    const snap = await getDoc(assetRef);
    if (!snap.exists()) {
      return;
    }
    const data = snap.data();
    if (data?.userId !== userId) {
      throw new Error(
        "You do not have permission to delete this screenshot asset.",
      );
    }

    if (data?.storagePath) {
      try {
        await deleteObject(storageRef(storage, data.storagePath));
      } catch (error: any) {
        if (error?.code !== "storage/object-not-found") {
          throw error;
        }
      }
    }
    await deleteDoc(assetRef);
  }

  async deleteScreenshotGenerationVersion(
    userId: string,
    versionId: string,
  ): Promise<void> {
    const versionRef = doc(db, STORE_SCREENSHOT_VERSIONS_COLLECTION, versionId);
    const versionSnap = await getDoc(versionRef);

    if (versionSnap.exists()) {
      const versionData = versionSnap.data();
      if (versionData?.userId !== userId) {
        throw new Error(
          "You do not have permission to delete this screenshot job.",
        );
      }
    }

    const assetsQuery = query(
      collection(db, STORE_SCREENSHOT_ASSETS_COLLECTION),
      where("versionId", "==", versionId),
      where("userId", "==", userId),
    );
    const assetsSnapshot = await getDocs(assetsQuery);

    for (const assetDoc of assetsSnapshot.docs) {
      const assetData = assetDoc.data();
      if (assetData?.storagePath) {
        try {
          await deleteObject(storageRef(storage, assetData.storagePath));
        } catch (error: any) {
          if (error?.code !== "storage/object-not-found") {
            throw error;
          }
        }
      }
      await deleteDoc(assetDoc.ref);
    }

    const zipExportsQuery = query(
      collection(db, STORE_SCREENSHOT_ZIP_EXPORTS_COLLECTION),
      where("versionId", "==", versionId),
      where("userId", "==", userId),
    );
    const zipExportsSnapshot = await getDocs(zipExportsQuery);

    for (const zipJobDoc of zipExportsSnapshot.docs) {
      const zipJobData = zipJobDoc.data();
      if (zipJobData?.storagePath) {
        try {
          await deleteObject(storageRef(storage, zipJobData.storagePath));
        } catch (error: any) {
          if (error?.code !== "storage/object-not-found") {
            throw error;
          }
        }
      }
      await deleteDoc(zipJobDoc.ref);
    }

    if (versionSnap.exists()) {
      const versionData = versionSnap.data();
      if (versionData?.zipStoragePath) {
        try {
          await deleteObject(storageRef(storage, versionData.zipStoragePath));
        } catch (error: any) {
          if (error?.code !== "storage/object-not-found") {
            throw error;
          }
        }
      }
      await deleteDoc(versionRef);
    }
  }

  async deleteScreenshotJobRecord(
    userId: string,
    jobId: string,
  ): Promise<void> {
    const jobRef = doc(db, STORE_SCREENSHOT_JOBS_COLLECTION, jobId);
    const jobSnap = await getDoc(jobRef);
    if (!jobSnap.exists()) {
      return;
    }

    const data = jobSnap.data();
    if (data?.userId !== userId) {
      throw new Error(
        "You do not have permission to delete this screenshot job.",
      );
    }

    await deleteDoc(jobRef);
  }

  async getStoreTemplateStyle(
    userId: string,
    appId: string,
    templateId: string,
  ): Promise<StoreScreenshotTemplateStyle | null> {
    const styleDocId = this.buildStyleDocId(userId, appId, templateId);
    const snap = await getDoc(
      doc(db, STORE_TEMPLATE_STYLES_COLLECTION, styleDocId),
    );
    if (!snap.exists()) {
      return null;
    }
    const data = snap.data();
    return (data?.style || null) as StoreScreenshotTemplateStyle | null;
  }

  getDimensionsByTargetPlatform(targetPlatform: ScreenshotTargetPlatform) {
    if (targetPlatform === "ios") {
      return ALL_DIMENSIONS.filter((dimension) => dimension.platform === "ios");
    }
    if (targetPlatform === "android") {
      return ALL_DIMENSIONS.filter(
        (dimension) => dimension.platform === "android",
      );
    }
    return ALL_DIMENSIONS;
  }

  getGeneratorDimensionsByTargetPlatform(
    targetPlatform: ScreenshotTargetPlatform,
  ) {
    if (targetPlatform === "ios") {
      return GENERATOR_IOS_DIMENSIONS;
    }
    if (targetPlatform === "android") {
      return ALL_DIMENSIONS.filter(
        (dimension) => dimension.platform === "android",
      );
    }
    return GENERATOR_DIMENSIONS;
  }

  async generateStoreScreenshots({
    appId,
    appName,
    appContext,
    targetPlatform,
    template,
    sourceImages,
    dimensions,
    model,
    copyMode = "ai",
    useScreenshotContext = true,
    locales = ["en"],
    previewDeviceId,
    onProgress,
    onLocaleGenerated,
    captureGeneratedResults = true,
  }: {
    appId: string;
    appName: string;
    appContext?: App | null;
    targetPlatform: ScreenshotTargetPlatform;
    template: StoreScreenshotTemplate;
    sourceImages: Array<{
      name: string;
      dataUrl: string;
      aiContext?: string;
      manualHeading?: string;
      manualSubtitle?: string;
      slotId?: string;
      slotName?: string;
      slotOrder?: number;
      slotStyle?: StoreScreenshotTemplateStyle;
      overlayImages?: Array<{
        id?: string;
        dataUrl: string;
        xRatio: number;
        yRatio: number;
        widthRatio: number;
        heightRatio: number;
        opacity?: number;
        borderRadiusRatio?: number;
      }>;
    }>;
    dimensions?: DeviceDimension[];
    model?: string;
    copyMode?: "ai" | "manual";
    useScreenshotContext?: boolean;
    locales?: string[];
    previewDeviceId?: StorePreviewDeviceId;
    onProgress?: (progress: StoreScreenshotGenerationProgress) => void;
    onLocaleGenerated?: (payload: {
      locale: string;
      screenshots: GeneratedStoreScreenshot[];
      completedCount: number;
      totalCount: number;
    }) => Promise<void> | void;
    captureGeneratedResults?: boolean;
  }): Promise<GeneratedStoreScreenshot[]> {
    const targetDimensions =
      dimensions && dimensions.length > 0
        ? dimensions
        : this.getDimensionsByTargetPlatform(targetPlatform);
    const generated: GeneratedStoreScreenshot[] = [];
    let totalGeneratedCount = 0;

    const selectedLocales = locales.length > 0 ? locales : ["en"];
    const totalCopySteps = sourceImages.length * selectedLocales.length;
    const totalRenderSteps =
      sourceImages.length * selectedLocales.length * targetDimensions.length;
    let completedCopySteps = 0;
    let completedRenderSteps = 0;
    const sourceBaseCopyByKey = new Map<
      string,
      { heading: string; subtitle: string }
    >();

    onProgress?.({
      stage: "preparing",
      message: "Preparing screenshot generation",
      completed: 0,
      total: totalCopySteps + totalRenderSteps,
      percent: 0,
    });

    for (const locale of selectedLocales) {
      const localeGenerated: GeneratedStoreScreenshot[] = [];
      for (
        let sourceIndex = 0;
        sourceIndex < sourceImages.length;
        sourceIndex += 1
      ) {
        const source = sourceImages[sourceIndex];
        const sourceImage = await this.loadImage(source.dataUrl);
        const overlayImages = Array.isArray(source.overlayImages)
          ? await Promise.all(
              source.overlayImages
                .filter((item) => item?.dataUrl)
                .map(async (item) => ({
                  id: item.id,
                  image: await this.loadImage(item.dataUrl),
                  xRatio: Number(item.xRatio) || 0.5,
                  yRatio: Number(item.yRatio) || 0.5,
                  widthRatio: Number(item.widthRatio) || 0.4,
                  heightRatio: Number(item.heightRatio) || 0.4,
                  opacity: typeof item.opacity === "number" ? item.opacity : 1,
                  borderRadiusRatio:
                    typeof item.borderRadiusRatio === "number"
                      ? item.borderRadiusRatio
                      : 0,
                })),
            )
          : [];
        const matchedSlotStyle =
          source.slotId && Array.isArray(template.screenSlots)
            ? template.screenSlots.find((slot) => slot.slotId === source.slotId)
                ?.style
            : undefined;
        const resolvedSourceStyle = matchedSlotStyle || source.slotStyle;
        const templateForSource = resolvedSourceStyle
          ? {
              ...template,
              style: resolvedSourceStyle,
            }
          : template;
        const headingMaxWords = SCREENSHOT_HEADING_MAX_WORDS;
        const subtitleMaxWords = SCREENSHOT_SUBTITLE_MAX_WORDS;
        const hasPreparedCopy =
          Boolean(source.manualHeading?.trim()) &&
          Boolean(source.manualSubtitle?.trim());
        const sourceKey = `${sourceIndex}:${source.slotId || source.name}`;
        let baseCopy = sourceBaseCopyByKey.get(sourceKey);
        if (!baseCopy) {
          if (copyMode === "manual" || hasPreparedCopy) {
            baseCopy = {
              heading: this.normalizeCopyText(
                (source.manualHeading || `${appName} Made Simple`).trim(),
              ),
              subtitle: this.normalizeCopyText(
                (
                  source.manualSubtitle ||
                  "Fast workflow. Clear results. Better outcomes."
                ).trim(),
              ),
            };
          } else {
            baseCopy = await this.generateScreenshotCopy({
              appName,
              appContext,
              targetPlatform,
              locale: "en",
              sourceName: source.name,
              sourceImageDataUrl: source.dataUrl,
              useScreenshotContext,
              context: source.aiContext,
              dimensions: targetDimensions,
              model,
            });
          }
          sourceBaseCopyByKey.set(sourceKey, baseCopy);
        }

        const copy = await this.translateManualScreenshotCopy({
          locale,
          appName,
          heading: baseCopy.heading,
          subtitle: baseCopy.subtitle,
          headingMaxWords,
          subtitleMaxWords,
          model,
        });
        completedCopySteps += 1;
        onProgress?.({
          stage: "copy",
          message: `Generating copy for ${source.name} (${locale})`,
          completed: completedCopySteps,
          total: totalCopySteps,
          percent: Math.round((completedCopySteps / totalCopySteps) * 100),
        });
        for (const dimension of targetDimensions) {
          const dataUrl = await this.renderTemplateScreenshot({
            appName,
            template: templateForSource,
            dimension,
            previewDeviceId,
            sourceImage,
            overlayImages,
            heading: copy.heading,
            subtitle: copy.subtitle,
          });

          const generatedScreenshot = {
            id: `ss_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            appId,
            appName,
            platform: dimension.platform,
            locale,
            dimension,
            sourceName: source.name,
            heading: copy.heading,
            subtitle: copy.subtitle,
            dataUrl,
            createdAt: new Date().toISOString(),
          };
          if (captureGeneratedResults) {
            generated.push(generatedScreenshot);
          }
          localeGenerated.push(generatedScreenshot);
          totalGeneratedCount += 1;
          completedRenderSteps += 1;
          const completedSteps = totalCopySteps + completedRenderSteps;
          const totalSteps = totalCopySteps + totalRenderSteps;
          onProgress?.({
            stage: "render",
            message: `Rendering ${dimension.name} for ${locale} (${completedRenderSteps}/${totalRenderSteps})`,
            completed: completedSteps,
            total: totalSteps,
            percent: Math.round((completedSteps / totalSteps) * 100),
          });
        }
      }
      await onLocaleGenerated?.({
        locale,
        screenshots: localeGenerated,
        completedCount: totalGeneratedCount,
        totalCount:
          sourceImages.length *
          selectedLocales.length *
          targetDimensions.length,
      });
    }

    onProgress?.({
      stage: "complete",
      message: `Generated ${totalGeneratedCount} screenshots`,
      completed: totalCopySteps + totalRenderSteps,
      total: totalCopySteps + totalRenderSteps,
      percent: 100,
    });

    return captureGeneratedResults ? generated : [];
  }

  async createStoreScreenshotsZipExport(
    appId: string,
    versionId: string,
  ): Promise<{
    ok: boolean;
    appId: string;
    versionId: string;
    totalAssets: number;
    storagePath: string;
    downloadUrl: string;
    fileName: string;
    zipCreatedAt: string;
  }> {
    return AIService.createStoreScreenshotsZip({
      appId,
      versionId,
    });
  }

  async requestStoreScreenshotsZipExportJob(
    appId: string,
    versionId: string,
    userId: string,
  ): Promise<StoreScreenshotZipExportJob> {
    const jobRef = doc(collection(db, STORE_SCREENSHOT_ZIP_EXPORTS_COLLECTION));
    const payload = {
      jobId: jobRef.id,
      appId,
      userId,
      versionId,
      status: "queued" as const,
      progress: {
        stage: "queued" as const,
        message: "Queued ZIP export",
        completed: 0,
        total: 100,
        percent: 0,
      },
      error: "",
      downloadUrl: "",
      fileName: "",
      storagePath: "",
      totalAssets: 0,
      zipCreatedAt: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(jobRef, payload);
    return {
      jobId: jobRef.id,
      appId,
      userId,
      versionId,
      status: "queued",
      progress: {
        stage: "queued",
        message: "Queued ZIP export",
        completed: 0,
        total: 100,
        percent: 0,
      },
      error: "",
      downloadUrl: "",
      fileName: "",
      storagePath: "",
      totalAssets: 0,
      zipCreatedAt: "",
    };
  }

  async getStoreScreenshotsZipExportJob(
    jobId: string,
  ): Promise<StoreScreenshotZipExportJob | null> {
    const snap = await getDoc(
      doc(db, STORE_SCREENSHOT_ZIP_EXPORTS_COLLECTION, jobId),
    );
    if (!snap.exists()) {
      return null;
    }
    const data = snap.data();
    return {
      jobId: data.jobId || snap.id,
      appId: data.appId || "",
      userId: data.userId || "",
      versionId: data.versionId || "",
      status: data.status || "queued",
      progress: {
        stage: data.progress?.stage || "queued",
        message: data.progress?.message || "Preparing ZIP export",
        completed: Number(data.progress?.completed || 0),
        total: Number(data.progress?.total || 100),
        percent: Number(data.progress?.percent || 0),
      },
      downloadUrl: data.downloadUrl || "",
      fileName: data.fileName || "",
      storagePath: data.storagePath || "",
      zipCreatedAt: data.zipCreatedAt
        ? this.convertTimestampToDate(data.zipCreatedAt).toISOString()
        : "",
      totalAssets: Number(data.totalAssets || 0),
      error: data.error || "",
      createdAt: data.createdAt
        ? this.convertTimestampToDate(data.createdAt).toISOString()
        : "",
      updatedAt: data.updatedAt
        ? this.convertTimestampToDate(data.updatedAt).toISOString()
        : "",
    };
  }

  async validateGooglePlayScreenshots(params: {
    appId: string;
    versionId: string;
    mode: GooglePlayScreenshotPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<GooglePlayScreenshotValidationResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayScreenshotValidationResponse>(
        VALIDATE_GOOGLE_PLAY_SCREENSHOTS_ENDPOINT,
        {
          appId: params.appId,
          versionId: params.versionId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        versionId: params.versionId,
        attemptedLocales: [],
        results: [],
      }
    );
  }

  async publishGooglePlayScreenshots(params: {
    appId: string;
    versionId: string;
    mode: GooglePlayScreenshotPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<GooglePlayScreenshotPublishResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayScreenshotPublishResponse>(
        PUBLISH_GOOGLE_PLAY_SCREENSHOTS_ENDPOINT,
        {
          appId: params.appId,
          versionId: params.versionId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        versionId: params.versionId,
        attemptedLocales: [],
        results: [],
        commitStatus: "not_committed",
      }
    );
  }

  async validateAppStoreConnectScreenshots(params: {
    appId: string;
    versionId: string;
    mode: GooglePlayScreenshotPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<GooglePlayScreenshotValidationResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayScreenshotValidationResponse>(
        VALIDATE_APP_STORE_CONNECT_SCREENSHOTS_ENDPOINT,
        {
          appId: params.appId,
          versionId: params.versionId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        versionId: params.versionId,
        attemptedLocales: [],
        results: [],
      }
    );
  }

  async publishAppStoreConnectScreenshots(params: {
    appId: string;
    versionId: string;
    mode: GooglePlayScreenshotPublishMode;
    locale?: string;
    locales?: string[];
  }): Promise<GooglePlayScreenshotPublishResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayScreenshotPublishResponse>(
        PUBLISH_APP_STORE_CONNECT_SCREENSHOTS_ENDPOINT,
        {
          appId: params.appId,
          versionId: params.versionId,
          mode: params.mode,
          ...(params.locale ? { locale: params.locale } : {}),
          ...(params.locales?.length ? { locales: params.locales } : {}),
        },
      );

    return (
      responseData || {
        success: true,
        packageName: "",
        versionId: params.versionId,
        attemptedLocales: [],
        results: [],
        commitStatus: "not_committed",
      }
    );
  }

  private parseScreenshotPublishJobData(
    data: Record<string, any>,
    documentId: string,
    store: "google_play" | "app_store_connect",
  ): GooglePlayScreenshotPublishResponse {
    return {
      success: Boolean(data.success),
      jobId: data.jobId || documentId,
      status: data.status || "queued",
      editId: data.editId || "",
      packageName: data.packageName || "",
      versionId: data.versionId || "",
      attemptedLocales: Array.isArray(data.attemptedLocales)
        ? data.attemptedLocales.map((item: unknown) => String(item))
        : [],
      totalLocales: Number(data.totalLocales || 0),
      processedLocales: Number(data.processedLocales || 0),
      totalBatches: Number(data.totalBatches || 0),
      completedBatches: Number(data.completedBatches || 0),
      results: Array.isArray(data.results)
        ? data.results.map((result: any) => ({
            locale: String(result?.locale || ""),
            playLocale: result?.playLocale
              ? String(result.playLocale)
              : undefined,
            status:
              result?.status === "success" ||
              result?.status === "error" ||
              result?.status === "skipped"
                ? result.status
                : "error",
            message: String(result?.message || ""),
            uploadedGroups: Array.isArray(result?.uploadedGroups)
              ? result.uploadedGroups.map((group: any) => ({
                  imageType: group?.imageType,
                  label: String(group?.label || ""),
                  assetCount: Number(group?.assetCount || 0),
                  currentCount:
                    typeof group?.currentCount === "number"
                      ? Number(group.currentCount)
                      : undefined,
                  currentImages: Array.isArray(group?.currentImages)
                    ? group.currentImages
                    : undefined,
                  proposedImages: Array.isArray(group?.proposedImages)
                    ? group.proposedImages
                    : undefined,
                }))
              : [],
          }))
        : [],
      batchStates:
        data.batchStates && typeof data.batchStates === "object"
          ? Object.entries(data.batchStates)
              .map(([batchIndex, batchState]: [string, any]) => ({
                batchIndex: Number(batchIndex),
                status:
                  batchState?.status === "queued" ||
                  batchState?.status === "processing" ||
                  batchState?.status === "completed" ||
                  batchState?.status === "partial" ||
                  batchState?.status === "failed"
                    ? batchState.status
                    : "queued",
                locales: Array.isArray(batchState?.locales)
                  ? batchState.locales.map((item: unknown) => String(item))
                  : [],
                commitStatus: (batchState?.commitStatus === "committed"
                  ? "committed"
                  : batchState?.commitStatus === "not_committed"
                    ? "not_committed"
                    : undefined) as "committed" | "not_committed" | undefined,
                message: batchState?.message ? String(batchState.message) : "",
                updatedAt: batchState?.updatedAt
                  ? String(batchState.updatedAt)
                  : "",
              }))
              .sort((a, b) => a.batchIndex - b.batchIndex)
          : [],
      commitStatus:
        data.commitStatus === "committed" ? "committed" : "not_committed",
      lastPublishedAt: data.lastPublishedAt
        ? this.convertTimestampToDate(data.lastPublishedAt).toISOString()
        : "",
      createdAt: data.createdAt
        ? this.convertTimestampToDate(data.createdAt).toISOString()
        : "",
      updatedAt: data.updatedAt
        ? this.convertTimestampToDate(data.updatedAt).toISOString()
        : "",
      message: data.message || "",
      store,
      appStoreConnectAppId: data.appStoreConnectAppId
        ? String(data.appStoreConnectAppId)
        : undefined,
      appStoreVersionId: data.appStoreVersionId
        ? String(data.appStoreVersionId)
        : undefined,
      appStoreVersionString: data.appStoreVersionString
        ? String(data.appStoreVersionString)
        : undefined,
    };
  }

  private async getScreenshotPublishJobFromCollection(
    collectionName: string,
    jobId: string,
    store: "google_play" | "app_store_connect",
  ): Promise<GooglePlayScreenshotPublishResponse | null> {
    const snap = await getDoc(doc(db, collectionName, jobId));
    if (!snap.exists()) {
      return null;
    }

    return this.parseScreenshotPublishJobData(
      snap.data() as Record<string, any>,
      snap.id,
      store,
    );
  }

  async getGooglePlayScreenshotPublishJob(
    jobId: string,
  ): Promise<GooglePlayScreenshotPublishResponse | null> {
    return this.getScreenshotPublishJobFromCollection(
      GOOGLE_PLAY_SCREENSHOT_PUBLISH_JOBS_COLLECTION,
      jobId,
      "google_play",
    );
  }

  async getAppStoreConnectScreenshotPublishJob(
    jobId: string,
  ): Promise<GooglePlayScreenshotPublishResponse | null> {
    return this.getScreenshotPublishJobFromCollection(
      APP_STORE_CONNECT_SCREENSHOT_PUBLISH_JOBS_COLLECTION,
      jobId,
      "app_store_connect",
    );
  }

  private async listScreenshotPublishJobsFromCollection(
    collectionName: string,
    store: "google_play" | "app_store_connect",
    params?: {
      appId?: string;
    },
  ): Promise<GooglePlayScreenshotPublishResponse[]> {
    const currentUser = auth.currentUser;
    const requesterUserId = currentUser?.email || currentUser?.uid || "";
    if (!requesterUserId) {
      return [];
    }

    const constraints: QueryConstraint[] = [];
    constraints.push(where("requesterUserId", "==", requesterUserId));
    if (params?.appId) {
      constraints.push(where("appId", "==", params.appId));
    }

    const snapshot = await getDocs(
      query(collection(db, collectionName), ...constraints),
    );

    return snapshot.docs
      .map((docSnap) =>
        this.parseScreenshotPublishJobData(
          docSnap.data() as Record<string, any>,
          docSnap.id,
          store,
        ),
      )
      .sort((left, right) => {
        const leftTime = new Date(
          left.updatedAt || left.createdAt || 0,
        ).getTime();
        const rightTime = new Date(
          right.updatedAt || right.createdAt || 0,
        ).getTime();
        return rightTime - leftTime;
      });
  }

  async listGooglePlayScreenshotPublishJobs(params?: {
    appId?: string;
  }): Promise<GooglePlayScreenshotPublishResponse[]> {
    return this.listScreenshotPublishJobsFromCollection(
      GOOGLE_PLAY_SCREENSHOT_PUBLISH_JOBS_COLLECTION,
      "google_play",
      params,
    );
  }

  async listAppStoreConnectScreenshotPublishJobs(params?: {
    appId?: string;
  }): Promise<GooglePlayScreenshotPublishResponse[]> {
    return this.listScreenshotPublishJobsFromCollection(
      APP_STORE_CONNECT_SCREENSHOT_PUBLISH_JOBS_COLLECTION,
      "app_store_connect",
      params,
    );
  }

  async downloadGeneratedScreenshot(
    screenshot: GeneratedStoreScreenshot,
    appName: string,
  ): Promise<void> {
    const link = document.createElement("a");
    link.href = screenshot.dataUrl;
    link.download = this.buildScreenshotFileName(screenshot, appName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async downloadGeneratedScreenshotsAsZip(
    screenshots: GeneratedStoreScreenshot[],
    appName: string,
  ): Promise<void> {
    const zip = new JSZip();
    const safeAppName = this.toSafeName(appName);

    for (const screenshot of screenshots) {
      const base64 = screenshot.dataUrl.split(",")[1];
      if (!base64) continue;
      const fileName = this.buildScreenshotFileName(screenshot, appName);
      zip.file(
        `${screenshot.platform}/${screenshot.locale}/${fileName}`,
        base64,
        {
          base64: true,
        },
      );
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeAppName}-store-screenshots.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async downloadStoredScreenshotsAsZip(
    assets: StoredStoreScreenshotAsset[],
    appName: string,
    options?: { versionNumber?: number },
  ): Promise<void> {
    if (!assets.length) {
      throw new Error(
        "No stored screenshot assets are available for ZIP download.",
      );
    }

    const zip = new JSZip();
    const folderCounters = new Map<string, number>();
    const safeAppName = this.toSafeName(appName);
    const sortedAssets = [...assets].sort((left, right) => {
      const leftFolder = this.buildStoredScreenshotDeviceFolderName(left);
      const rightFolder = this.buildStoredScreenshotDeviceFolderName(right);

      return (
        leftFolder.localeCompare(rightFolder) ||
        left.sourceName.localeCompare(right.sourceName) ||
        left.storagePath.localeCompare(right.storagePath)
      );
    });

    const concurrency = 8;
    for (let index = 0; index < sortedAssets.length; index += concurrency) {
      const batch = sortedAssets.slice(index, index + concurrency);
      const batchResults = await Promise.all(
        batch.map(async (asset) => {
          const response = await fetch(asset.downloadUrl);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch screenshot asset ${asset.assetId} for browser ZIP.`,
            );
          }

          return {
            asset,
            blob: await response.blob(),
          };
        }),
      );

      batchResults.forEach(({ asset, blob }) => {
        const folderPath = this.buildStoredScreenshotDeviceFolderName(asset);
        const currentIndex = folderCounters.get(folderPath) || 0;
        const ext = this.inferFileExtension(
          asset.storagePath || asset.downloadUrl,
        );
        const fileName = `${currentIndex}.${ext}`;

        folderCounters.set(folderPath, currentIndex + 1);
        zip.folder(folderPath)?.file(fileName, blob);
      });
    }

    const blob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      streamFiles: true,
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const versionSuffix = options?.versionNumber
      ? `-store-screenshots-v${options.versionNumber}`
      : "-store-screenshots";

    link.href = url;
    link.download = `${safeAppName}${versionSuffix}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async renderStoreTemplatePreview({
    appName,
    template,
    dimension,
    previewDeviceId,
    sourceImageDataUrl,
    overlayImages = [],
    heading,
    subtitle,
  }: {
    appName: string;
    template: StoreScreenshotTemplate;
    dimension: DeviceDimension;
    previewDeviceId?: StorePreviewDeviceId;
    sourceImageDataUrl: string;
    overlayImages?: Array<{
      id?: string;
      dataUrl: string;
      xRatio: number;
      yRatio: number;
      widthRatio: number;
      heightRatio: number;
      opacity?: number;
      borderRadiusRatio?: number;
    }>;
    heading: string;
    subtitle: string;
  }): Promise<string> {
    const sourceImage = await this.loadImage(sourceImageDataUrl);
    const loadedOverlayImages = Array.isArray(overlayImages)
      ? await Promise.all(
          overlayImages
            .filter((item) => item?.dataUrl)
            .map(async (item) => ({
              id: item.id,
              image: await this.loadImage(item.dataUrl),
              xRatio: Number(item.xRatio) || 0.5,
              yRatio: Number(item.yRatio) || 0.5,
              widthRatio: Number(item.widthRatio) || 0.4,
              heightRatio: Number(item.heightRatio) || 0.4,
              opacity: typeof item.opacity === "number" ? item.opacity : 1,
              borderRadiusRatio:
                typeof item.borderRadiusRatio === "number"
                  ? item.borderRadiusRatio
                  : 0,
            })),
        )
      : [];
    return this.renderTemplateScreenshot({
      appName,
      template,
      dimension,
      previewDeviceId,
      sourceImage,
      overlayImages: loadedOverlayImages,
      heading,
      subtitle,
    });
  }

  async generateTemplatePreviewCopy({
    appName,
    appContext,
    targetPlatform,
    locale = "en",
    sourceImageDataUrl,
    context,
    dimensions,
    model,
  }: {
    appName: string;
    appContext?: App | null;
    targetPlatform: ScreenshotTargetPlatform;
    locale?: string;
    sourceImageDataUrl?: string;
    context?: string;
    dimensions: DeviceDimension[];
    model?: string;
  }): Promise<{ heading: string; subtitle: string }> {
    return this.generateScreenshotCopy({
      appName,
      appContext,
      targetPlatform,
      locale,
      sourceName: "template_preview",
      sourceImageDataUrl,
      useScreenshotContext: Boolean(sourceImageDataUrl),
      context,
      dimensions,
      model,
    });
  }

  async generateStoreScreenshotSourceCopy({
    appName,
    appContext,
    targetPlatform,
    sourceName,
    sourceImageDataUrl,
    useScreenshotContext = true,
    context,
    dimensions,
    model,
  }: {
    appName: string;
    appContext?: App | null;
    targetPlatform: ScreenshotTargetPlatform;
    sourceName: string;
    sourceImageDataUrl?: string;
    useScreenshotContext?: boolean;
    context?: string;
    dimensions: DeviceDimension[];
    model?: string;
  }): Promise<{ heading: string; subtitle: string }> {
    return this.generateScreenshotCopy({
      appName,
      appContext,
      targetPlatform,
      locale: "en",
      sourceName,
      sourceImageDataUrl,
      useScreenshotContext,
      context,
      dimensions,
      model,
    });
  }

  private async translateManualScreenshotCopy({
    locale,
    appName,
    heading,
    subtitle,
    headingMaxWords,
    subtitleMaxWords,
    model,
  }: {
    locale: string;
    appName: string;
    heading: string;
    subtitle: string;
    headingMaxWords: number;
    subtitleMaxWords: number;
    model?: string;
  }): Promise<{ heading: string; subtitle: string }> {
    if (this.isEnglishLocale(locale)) {
      return {
        heading,
        subtitle,
      };
    }

    const schema = {
      type: "object",
      properties: {
        heading: { type: "string" },
        subtitle: { type: "string" },
      },
      required: ["heading", "subtitle"],
    };

    const prompt = [
      "Translate app store screenshot copy into the requested locale.",
      `App name: ${appName}`,
      `Target locale: ${locale}`,
      `Heading source: ${heading}`,
      `Subtitle source: ${subtitle}`,
      "Constraints:",
      `- Keep the heading to ${headingMaxWords} words or fewer.`,
      `- Keep the subtitle to ${subtitleMaxWords} words or fewer.`,
      "- Preserve the original meaning and conversion intent.",
      "- Do not add quotation marks, emojis, hashtags, or trailing punctuation.",
      "- Return natural, idiomatic marketing copy for the locale.",
    ].join("\n");

    try {
      const translated = await AIService.generateJSON<{
        heading?: string;
        subtitle?: string;
      }>(prompt, {
        model: model || AIService.getDefaultModel(),
        temperature: 0.2,
        structuredOutputSchema: schema,
        structuredOutputName: "translated_screenshot_copy",
      });

      const translatedHeading = this.normalizeCopyText(
        translated?.heading || heading,
      );
      const translatedSubtitle = this.normalizeCopyText(
        translated?.subtitle || subtitle,
      );

      return {
        heading: translatedHeading,
        subtitle: translatedSubtitle,
      };
    } catch {
      return {
        heading: this.normalizeCopyText(heading),
        subtitle: this.normalizeCopyText(subtitle),
      };
    }
  }

  private isEnglishLocale(locale: string): boolean {
    const normalized = String(locale || "")
      .trim()
      .replaceAll("_", "-")
      .toLowerCase();
    return normalized === "en" || normalized.startsWith("en-");
  }

  private async renderTemplateScreenshot({
    appName,
    template,
    dimension,
    previewDeviceId,
    sourceImage,
    overlayImages = [],
    heading,
    subtitle,
  }: {
    appName: string;
    template: StoreScreenshotTemplate;
    dimension: DeviceDimension;
    previewDeviceId?: StorePreviewDeviceId;
    sourceImage: HTMLImageElement;
    overlayImages?: Array<{
      id?: string;
      image: HTMLImageElement;
      xRatio: number;
      yRatio: number;
      widthRatio: number;
      heightRatio: number;
      opacity?: number;
      borderRadiusRatio?: number;
    }>;
    heading: string;
    subtitle: string;
  }): Promise<string> {
    const layout = (template.layout || template.id) as StoreTemplateLayoutId;
    if (
      layout !== "simple_heading_device" &&
      layout !== "feature_spotlight_device"
    ) {
      throw new Error(`Unsupported screenshot template layout: ${layout}`);
    }

    const style = this.resolveTemplateStyle(template.style);
    const canvas = document.createElement("canvas");
    canvas.width = dimension.width;
    canvas.height = dimension.height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to initialize screenshot canvas context.");
    }

    const headingFontPx = Math.max(
      42,
      Math.round(canvas.width * style.headingSizeRatio),
    );
    const subtitleFontPx = Math.max(
      24,
      Math.round(canvas.width * style.subtitleSizeRatio),
    );
    await this.ensureCanvasFontsLoaded(style, headingFontPx, subtitleFontPx);
    if (
      template.generationMode === "screen_slots" &&
      this.hasAdvancedComposition(style)
    ) {
      this.drawAdvancedCompositionScene({
        context,
        canvas,
        style,
        sourceImage,
        overlayImages,
        heading,
        subtitle,
        headingFontPx,
        subtitleFontPx,
      });
      return canvas.toDataURL("image/png");
    }

    if (!style.showDeviceFrame) {
      this.paintTemplateBackground(context, canvas.width, canvas.height, style);
      const imageScale = Math.max(
        canvas.width / sourceImage.width,
        canvas.height / sourceImage.height,
      );
      const imageWidth = sourceImage.width * imageScale;
      const imageHeight = sourceImage.height * imageScale;
      const imageX = (canvas.width - imageWidth) / 2;
      const imageY = (canvas.height - imageHeight) / 2;
      context.drawImage(sourceImage, imageX, imageY, imageWidth, imageHeight);
      this.drawOverlayImages(context, canvas, overlayImages);
      this.drawAbsoluteTextLayer({
        context,
        canvas,
        style,
        heading,
        subtitle,
        headingFontPx,
        subtitleFontPx,
      });
      return canvas.toDataURL("image/png");
    }

    const frameProfile = this.resolveFrameProfile(
      dimension,
      style.androidDeviceFrameId,
      previewDeviceId,
    );
    const frameSize = this.resolveFrameBounds(canvas, style, frameProfile);
    const frameWidth = frameSize.width;
    const frameHeight = frameSize.height;
    const frameX =
      layout === "feature_spotlight_device"
        ? Math.round(canvas.width * 0.34)
        : Math.round((canvas.width - frameWidth) / 2);
    const baseFrameY = Math.round(canvas.height * style.frameTopRatio);
    const maxFrameY = Math.max(
      baseFrameY,
      canvas.height - frameHeight - Math.round(canvas.height * 0.03),
    );
    let frameY = baseFrameY;
    const frameRadius = Math.round(frameWidth * frameProfile.frameRadiusRatio);

    if (layout === "feature_spotlight_device") {
      this.paintTemplateBackground(context, canvas.width, canvas.height, style);

      context.globalAlpha = 0.22;
      context.fillStyle = "#93C5FD";
      context.beginPath();
      context.arc(
        canvas.width * 0.18,
        canvas.height * 0.2,
        canvas.width * 0.22,
        0,
        Math.PI * 2,
      );
      context.fill();
      context.fillStyle = "#60A5FA";
      context.beginPath();
      context.arc(
        canvas.width * 0.86,
        canvas.height * 0.12,
        canvas.width * 0.16,
        0,
        Math.PI * 2,
      );
      context.fill();
      context.globalAlpha = 1;

      context.fillStyle = "rgba(255,255,255,0.16)";
      this.drawRoundRect(
        context,
        Math.round(canvas.width * 0.06),
        Math.round(canvas.height * 0.075),
        Math.round(canvas.width * 0.26),
        Math.round(canvas.height * 0.046),
        Math.round(canvas.width * 0.02),
      );
      context.fill();
      context.fillStyle = "#E2E8F0";
      context.textAlign = "left";
      context.font = `600 ${Math.max(
        18,
        Math.round(canvas.width * 0.018),
      )}px "Inter", "Segoe UI", sans-serif`;
      context.fillText(
        "FEATURE SPOTLIGHT",
        Math.round(canvas.width * 0.08),
        Math.round(canvas.height * 0.106),
      );

      const headingTop = Math.round(canvas.height * style.headingTopRatio);
      const headingLeft = Math.round(canvas.width * 0.08);
      const textRightLimit = frameX - Math.round(canvas.width * 0.045);
      const maxTextWidth = Math.max(
        Math.round(canvas.width * 0.24),
        textRightLimit - headingLeft,
      );
      const frameGap = Math.round(canvas.height * 0.035);
      const textBottom = maxFrameY - frameGap;
      const blockGap = Math.max(
        12,
        Math.round(canvas.height * style.subtitleOffsetRatio * 0.55),
      );

      context.fillStyle = style.headingColor;
      context.textAlign = "left";
      context.textBaseline = "top";
      const headingLayout = this.fitWrappedTextBlock(context, {
        text: style.useAbsoluteTextPlacement ? "" : heading,
        fontStyle: style.headingFontStyle,
        fontWeight: style.headingWeight,
        fontSizePx: headingFontPx,
        minFontSizePx: Math.max(18, Math.round(headingFontPx * 0.42)),
        fontFamily: style.headingFontFamily,
        maxWidth: maxTextWidth,
        maxHeight: Math.max(
          42,
          textBottom -
            headingTop -
            (style.showSubtitle ? blockGap + subtitleFontPx : 0),
        ),
        maxLines: 2,
        lineHeightMultiplier: 1.08,
      });
      context.font = this.buildCanvasFont(
        style.headingFontStyle,
        style.headingWeight,
        headingLayout.fontSizePx,
        style.headingFontFamily,
      );
      this.drawWrappedTextLines(
        context,
        headingLayout.lines,
        headingLeft,
        headingTop,
        headingLayout.lineHeight,
      );

      context.fillStyle = style.subtitleColor;
      let textBlockBottom = headingTop + headingLayout.height;
      if (style.showSubtitle && !style.useAbsoluteTextPlacement) {
        const subtitleTop = headingTop + headingLayout.height + blockGap;
        const subtitleLayout = this.fitWrappedTextBlock(context, {
          text: subtitle,
          fontStyle: style.subtitleFontStyle,
          fontWeight: style.subtitleWeight,
          fontSizePx: subtitleFontPx,
          minFontSizePx: Math.max(12, Math.round(subtitleFontPx * 0.52)),
          fontFamily: style.subtitleFontFamily,
          maxWidth: maxTextWidth,
          maxHeight: Math.max(28, textBottom - subtitleTop),
          maxLines: 1,
          lineHeightMultiplier: 1.14,
        });
        context.font = this.buildCanvasFont(
          style.subtitleFontStyle,
          style.subtitleWeight,
          subtitleLayout.fontSizePx,
          style.subtitleFontFamily,
        );
        this.drawWrappedTextLines(
          context,
          subtitleLayout.lines,
          headingLeft,
          subtitleTop,
          subtitleLayout.lineHeight,
        );
        textBlockBottom = subtitleTop + subtitleLayout.height;
      }
      frameY = Math.max(
        baseFrameY,
        Math.min(maxFrameY, textBlockBottom + frameGap),
      );
    } else {
      this.paintTemplateBackground(context, canvas.width, canvas.height, style);

      const headingTop = Math.round(canvas.height * style.headingTopRatio);
      const maxTextWidth = Math.round(canvas.width * 0.84);
      const frameGap = Math.round(canvas.height * 0.03);
      const textBottom = maxFrameY - frameGap;
      const blockGap = Math.max(
        12,
        Math.round(canvas.height * style.subtitleOffsetRatio * 0.55),
      );

      context.fillStyle = style.headingColor;
      context.textAlign = "center";
      context.textBaseline = "top";
      const headingLayout = this.fitWrappedTextBlock(context, {
        text: style.useAbsoluteTextPlacement ? "" : heading,
        fontStyle: style.headingFontStyle,
        fontWeight: style.headingWeight,
        fontSizePx: headingFontPx,
        minFontSizePx: Math.max(18, Math.round(headingFontPx * 0.42)),
        fontFamily: style.headingFontFamily,
        maxWidth: maxTextWidth,
        maxHeight: Math.max(
          42,
          textBottom -
            headingTop -
            (style.showSubtitle ? blockGap + subtitleFontPx : 0),
        ),
        maxLines: 2,
        lineHeightMultiplier: 1.08,
      });
      context.font = this.buildCanvasFont(
        style.headingFontStyle,
        style.headingWeight,
        headingLayout.fontSizePx,
        style.headingFontFamily,
      );
      this.drawWrappedTextLines(
        context,
        headingLayout.lines,
        canvas.width / 2,
        headingTop,
        headingLayout.lineHeight,
      );

      context.fillStyle = style.subtitleColor;
      let textBlockBottom = headingTop + headingLayout.height;
      if (style.showSubtitle && !style.useAbsoluteTextPlacement) {
        const resolvedSubtitleFontPx = Math.max(26, subtitleFontPx);
        const subtitleTop = headingTop + headingLayout.height + blockGap;
        const subtitleLayout = this.fitWrappedTextBlock(context, {
          text: subtitle,
          fontStyle: style.subtitleFontStyle,
          fontWeight: style.subtitleWeight,
          fontSizePx: resolvedSubtitleFontPx,
          minFontSizePx: Math.max(
            12,
            Math.round(resolvedSubtitleFontPx * 0.52),
          ),
          fontFamily: style.subtitleFontFamily,
          maxWidth: Math.round(canvas.width * 0.76),
          maxHeight: Math.max(28, textBottom - subtitleTop),
          maxLines: 1,
          lineHeightMultiplier: 1.14,
        });
        context.font = this.buildCanvasFont(
          style.subtitleFontStyle,
          style.subtitleWeight,
          subtitleLayout.fontSizePx,
          style.subtitleFontFamily,
        );
        this.drawWrappedTextLines(
          context,
          subtitleLayout.lines,
          canvas.width / 2,
          subtitleTop,
          subtitleLayout.lineHeight,
        );
        textBlockBottom = subtitleTop + subtitleLayout.height;
      }
      frameY = Math.max(
        baseFrameY,
        Math.min(maxFrameY, textBlockBottom + frameGap),
      );
    }

    if (frameProfile.platform === "ios") {
      const outerGradient = context.createLinearGradient(
        frameX,
        frameY,
        frameX + frameWidth,
        frameY + frameHeight,
      );
      if (frameProfile.formFactor === "tablet") {
        outerGradient.addColorStop(0, "#14161B");
        outerGradient.addColorStop(0.38, "#050608");
        outerGradient.addColorStop(0.72, "#1A1D22");
        outerGradient.addColorStop(1, "#090B0E");
        context.shadowColor = "rgba(0, 0, 0, 0.34)";
        context.shadowBlur = Math.max(18, Math.round(frameWidth * 0.028));
        context.shadowOffsetY = Math.max(10, Math.round(frameWidth * 0.012));
      } else {
        outerGradient.addColorStop(0, "#D2D7E0");
        outerGradient.addColorStop(0.35, "#9CA4B5");
        outerGradient.addColorStop(0.65, "#E0E5EE");
        outerGradient.addColorStop(1, "#8E97A9");
        context.shadowColor = "rgba(0, 0, 0, 0.24)";
        context.shadowBlur = Math.max(14, Math.round(frameWidth * 0.03));
        context.shadowOffsetY = Math.max(8, Math.round(frameWidth * 0.01));
      }
      context.fillStyle = outerGradient;
    } else {
      const androidGradient = context.createLinearGradient(
        frameX,
        frameY,
        frameX + frameWidth,
        frameY + frameHeight,
      );
      androidGradient.addColorStop(0, frameProfile.shellPrimary);
      androidGradient.addColorStop(0.55, frameProfile.shellSecondary);
      androidGradient.addColorStop(1, frameProfile.shellTertiary);
      context.fillStyle = androidGradient;
      context.shadowColor = "rgba(0, 0, 0, 0.22)";
      context.shadowBlur = Math.max(12, Math.round(frameWidth * 0.022));
      context.shadowOffsetY = Math.max(7, Math.round(frameWidth * 0.008));
    }
    this.drawRoundRect(
      context,
      frameX,
      frameY,
      frameWidth,
      frameHeight,
      frameRadius,
    );
    context.fill();
    context.shadowColor = "transparent";
    context.shadowBlur = 0;
    context.shadowOffsetY = 0;

    if (
      frameProfile.platform === "ios" &&
      frameProfile.formFactor === "phone"
    ) {
      const buttonColor = "#778196";
      const sideInset = Math.max(4, Math.round(frameWidth * 0.005));
      const volumeButtonW = Math.max(3, Math.round(frameWidth * 0.008));
      const actionButtonH = Math.round(frameHeight * 0.04);
      const volumeButtonH = Math.round(frameHeight * 0.085);
      const lockButtonH = Math.round(frameHeight * 0.13);

      context.fillStyle = buttonColor;
      this.drawRoundRect(
        context,
        frameX - sideInset,
        frameY + Math.round(frameHeight * 0.17),
        volumeButtonW,
        actionButtonH,
        volumeButtonW,
      );
      context.fill();
      this.drawRoundRect(
        context,
        frameX - sideInset,
        frameY + Math.round(frameHeight * 0.26),
        volumeButtonW,
        volumeButtonH,
        volumeButtonW,
      );
      context.fill();
      this.drawRoundRect(
        context,
        frameX + frameWidth + sideInset - volumeButtonW,
        frameY + Math.round(frameHeight * 0.22),
        volumeButtonW,
        lockButtonH,
        volumeButtonW,
      );
      context.fill();
    } else if (
      frameProfile.platform === "android" &&
      frameProfile.screenCutout === "punch_hole"
    ) {
      const holeRadius = Math.round(
        frameWidth * frameProfile.cameraRadiusRatio,
      );
      const cy = Math.round(frameY + frameHeight * frameProfile.cameraTopRatio);
      const cx = Math.round(frameX + frameWidth * frameProfile.cameraXRatio);
      context.fillStyle = "#080a0d";
      context.beginPath();
      context.arc(cx, cy, holeRadius, 0, Math.PI * 2);
      context.fill();

      context.fillStyle = "rgba(255,255,255,0.08)";
      context.beginPath();
      context.arc(
        cx - Math.max(1, Math.round(holeRadius * 0.3)),
        cy - Math.max(1, Math.round(holeRadius * 0.3)),
        Math.max(1, Math.round(holeRadius * 0.35)),
        0,
        Math.PI * 2,
      );
      context.fill();
    }

    if (frameProfile.screenCutout === "camera_dot") {
      const cameraRadius = Math.max(
        3,
        Math.round(frameWidth * frameProfile.cameraRadiusRatio),
      );
      const cameraX = Math.round(
        frameX + frameWidth * frameProfile.cameraXRatio,
      );
      const cameraY = Math.round(
        frameY + frameHeight * frameProfile.cameraTopRatio,
      );
      context.fillStyle =
        frameProfile.platform === "ios" ? "#0A0D11" : "#0D1015";
      context.beginPath();
      context.arc(cameraX, cameraY, cameraRadius, 0, Math.PI * 2);
      context.fill();

      context.fillStyle = "rgba(255,255,255,0.16)";
      context.beginPath();
      context.arc(
        cameraX - Math.max(1, Math.round(cameraRadius * 0.35)),
        cameraY - Math.max(1, Math.round(cameraRadius * 0.35)),
        Math.max(1, Math.round(cameraRadius * 0.3)),
        0,
        Math.PI * 2,
      );
      context.fill();
    }

    const bezel = Math.round(frameWidth * frameProfile.bezelRatio);
    const screenX = frameX + bezel;
    const screenY =
      frameY + Math.round(bezel * frameProfile.topInsetMultiplier);
    const screenWidth = frameWidth - bezel * 2;
    const screenHeight =
      frameHeight - Math.round(bezel * frameProfile.heightInsetMultiplier);
    const screenRadius = Math.round(
      frameRadius * frameProfile.screenRadiusMultiplier,
    );

    if (frameProfile.platform === "ios") {
      context.save();
      context.strokeStyle =
        frameProfile.formFactor === "tablet"
          ? "rgba(255,255,255,0.12)"
          : "rgba(255,255,255,0.28)";
      context.lineWidth =
        frameProfile.formFactor === "tablet"
          ? Math.max(1, Math.round(frameWidth * 0.0022))
          : Math.max(2, Math.round(frameWidth * 0.004));
      this.drawRoundRect(
        context,
        frameX + 2,
        frameY + 2,
        frameWidth - 4,
        frameHeight - 4,
        frameRadius,
      );
      context.stroke();
      context.restore();

      if (frameProfile.formFactor === "tablet") {
        context.save();
        context.strokeStyle = "rgba(255,255,255,0.06)";
        context.lineWidth = Math.max(1, Math.round(frameWidth * 0.0015));
        this.drawRoundRect(
          context,
          screenX - Math.max(2, Math.round(bezel * 0.34)),
          screenY - Math.max(2, Math.round(bezel * 0.34)),
          screenWidth + Math.max(4, Math.round(bezel * 0.68)),
          screenHeight + Math.max(4, Math.round(bezel * 0.68)),
          Math.max(screenRadius, Math.round(frameRadius * 0.42)),
        );
        context.stroke();
        context.restore();
      }
    }

    context.save();
    this.drawRoundRect(
      context,
      screenX,
      screenY,
      screenWidth,
      screenHeight,
      screenRadius,
    );
    context.clip();

    const imageScale = Math.max(
      screenWidth / sourceImage.width,
      screenHeight / sourceImage.height,
    );
    const imageWidth = sourceImage.width * imageScale;
    const imageHeight = sourceImage.height * imageScale;
    const imageX = screenX + (screenWidth - imageWidth) / 2;
    const imageY = screenY + (screenHeight - imageHeight) / 2;
    context.drawImage(sourceImage, imageX, imageY, imageWidth, imageHeight);

    context.restore();

    if (
      frameProfile.platform === "ios" &&
      frameProfile.screenCutout === "dynamic_island"
    ) {
      const islandWidth = Math.round(frameWidth * 0.27);
      const islandHeight = Math.round(frameHeight * 0.036);
      const islandX = Math.round(frameX + (frameWidth - islandWidth) / 2);
      const islandY = Math.round(screenY + screenHeight * 0.018);
      context.fillStyle = "#050608";
      this.drawRoundRect(
        context,
        islandX,
        islandY,
        islandWidth,
        islandHeight,
        Math.round(islandHeight / 2),
      );
      context.fill();

      context.fillStyle = "#1A1D24";
      context.beginPath();
      context.arc(
        islandX + Math.round(islandWidth * 0.79),
        islandY + Math.round(islandHeight * 0.5),
        Math.max(2, Math.round(islandHeight * 0.18)),
        0,
        Math.PI * 2,
      );
      context.fill();

      context.fillStyle = "rgba(255,255,255,0.10)";
      this.drawRoundRect(
        context,
        islandX + Math.round(islandWidth * 0.12),
        islandY + Math.round(islandHeight * 0.32),
        Math.round(islandWidth * 0.22),
        Math.max(2, Math.round(islandHeight * 0.12)),
        Math.max(2, Math.round(islandHeight * 0.08)),
      );
      context.fill();

      context.fillStyle = "rgba(255,255,255,0.16)";
      this.drawRoundRect(
        context,
        frameX + Math.round(frameWidth * 0.42),
        frameY + Math.round(frameHeight * 0.013),
        Math.round(frameWidth * 0.16),
        Math.max(2, Math.round(frameHeight * 0.005)),
        Math.max(2, Math.round(frameHeight * 0.003)),
      );
      context.fill();
    }

    if (
      frameProfile.platform === "ios" &&
      frameProfile.formFactor === "tablet"
    ) {
      const indicatorWidth = Math.round(screenWidth * 0.18);
      const indicatorHeight = Math.max(5, Math.round(screenHeight * 0.008));
      const indicatorX = Math.round(
        screenX + (screenWidth - indicatorWidth) / 2,
      );
      const indicatorY = Math.round(
        screenY + screenHeight - indicatorHeight - screenHeight * 0.022,
      );
      context.fillStyle = "rgba(255,255,255,0.92)";
      this.drawRoundRect(
        context,
        indicatorX,
        indicatorY,
        indicatorWidth,
        indicatorHeight,
        Math.round(indicatorHeight / 2),
      );
      context.fill();
    }

    this.drawOverlayImages(context, canvas, overlayImages);
    if (style.useAbsoluteTextPlacement) {
      this.drawAbsoluteTextLayer({
        context,
        canvas,
        style,
        heading,
        subtitle,
        headingFontPx,
        subtitleFontPx,
      });
    }

    return canvas.toDataURL("image/png");
  }

  private resolveTemplateStyle(
    style?: Partial<StoreScreenshotTemplateStyle>,
  ): StoreScreenshotTemplateStyle {
    const rawMask =
      style?.mainImageMask && typeof style.mainImageMask === "object"
        ? style.mainImageMask
        : {};
    const rawTransform =
      style?.mainImageTransform && typeof style.mainImageTransform === "object"
        ? style.mainImageTransform
        : {};
    const rawLayerStack = Array.isArray(style?.layerStack)
      ? style?.layerStack
      : this.DEFAULT_TEMPLATE_STYLE.layerStack;
    return {
      ...this.DEFAULT_TEMPLATE_STYLE,
      ...(style || {}),
      mainImageMask: {
        ...this.DEFAULT_TEMPLATE_STYLE.mainImageMask,
        ...(rawMask || {}),
      },
      mainImageTransform: {
        ...this.DEFAULT_TEMPLATE_STYLE.mainImageTransform,
        ...(rawTransform || {}),
      },
      layerStack: this.normalizeLayerStack(rawLayerStack || []),
    };
  }

  private hasAdvancedComposition(style: StoreScreenshotTemplateStyle): boolean {
    const hasLayerStack =
      Array.isArray(style.layerStack) &&
      style.layerStack.length > 0 &&
      !this.isLegacyDefaultLayerStack(style.layerStack);
    const defaultMask = this.DEFAULT_TEMPLATE_STYLE.mainImageMask;
    const mask = style.mainImageMask || defaultMask;
    const hasCustomMask =
      mask.type !== defaultMask.type ||
      Math.abs(
        (mask.radiusRatio ?? defaultMask.radiusRatio) - defaultMask.radiusRatio,
      ) > 0.0001 ||
      Math.abs(
        (mask.insetRatio ?? defaultMask.insetRatio) - defaultMask.insetRatio,
      ) > 0.0001 ||
      Math.abs(
        (mask.waveDepthRatio ?? defaultMask.waveDepthRatio) -
          defaultMask.waveDepthRatio,
      ) > 0.0001;
    const defaultTransform = this.DEFAULT_TEMPLATE_STYLE.mainImageTransform;
    const transform = style.mainImageTransform || defaultTransform;
    const hasCustomTransform =
      Math.abs(
        (transform.xRatio ?? defaultTransform.xRatio) - defaultTransform.xRatio,
      ) > 0.0001 ||
      Math.abs(
        (transform.yRatio ?? defaultTransform.yRatio) - defaultTransform.yRatio,
      ) > 0.0001 ||
      Math.abs(
        (transform.scale ?? defaultTransform.scale) - defaultTransform.scale,
      ) > 0.0001 ||
      Math.abs(
        (transform.rotationDeg ?? defaultTransform.rotationDeg) -
          defaultTransform.rotationDeg,
      ) > 0.0001 ||
      Math.abs(
        (transform.opacity ?? defaultTransform.opacity) -
          defaultTransform.opacity,
      ) > 0.0001;
    return hasLayerStack || hasCustomMask || hasCustomTransform;
  }

  private isLegacyDefaultLayerStack(
    layers: StoreScreenshotTemplateStyle["layerStack"],
  ): boolean {
    if (!Array.isArray(layers)) return true;
    const normalized = layers
      .filter((layer) => layer.kind !== "overlay_image")
      .sort((left, right) => (left.zIndex || 0) - (right.zIndex || 0));
    if (normalized.length === 0) return true;
    if (normalized.length !== 4) return false;
    const kinds = normalized.map((layer) => layer.kind);
    const expected: StoreCompositionLayerKind[] = [
      "background",
      "main_image",
      "text_heading",
      "text_subtitle",
    ];
    return (
      kinds.every((kind, index) => kind === expected[index]) &&
      normalized.every((layer) => layer.visible !== false)
    );
  }

  private normalizeLayerStack(
    input: Array<{
      id?: string;
      kind?: string;
      visible?: boolean;
      zIndex?: number;
      refId?: string;
    }>,
  ): StoreScreenshotTemplateStyle["layerStack"] {
    const defaultKinds = new Set<StoreCompositionLayerKind>([
      "background",
      "main_image",
      "text_heading",
      "text_subtitle",
    ]);
    const normalized = input
      .map((item, index) => ({
        id:
          typeof item?.id === "string" && item.id.trim()
            ? item.id.trim()
            : `layer_${index + 1}`,
        kind: (item?.kind === "background" ||
        item?.kind === "main_image" ||
        item?.kind === "overlay_image" ||
        item?.kind === "text_heading" ||
        item?.kind === "text_subtitle"
          ? item.kind
          : "main_image") as StoreCompositionLayerKind,
        visible: item?.visible !== false,
        zIndex:
          typeof item?.zIndex === "number" && Number.isFinite(item.zIndex)
            ? item.zIndex
            : index,
        refId: typeof item?.refId === "string" ? item.refId : undefined,
      }))
      .sort((left, right) => left.zIndex - right.zIndex);

    normalized.forEach((layer) => {
      if (layer.kind !== "overlay_image") {
        defaultKinds.delete(layer.kind);
      }
    });

    if (defaultKinds.size > 0) {
      let zIndex = normalized.length;
      defaultKinds.forEach((kind) => {
        normalized.push({
          id: kind,
          kind,
          visible: true,
          zIndex,
          refId: undefined,
        });
        zIndex += 1;
      });
    }
    return normalized.sort((left, right) => left.zIndex - right.zIndex);
  }

  private drawAdvancedCompositionScene({
    context,
    canvas,
    style,
    sourceImage,
    overlayImages,
    heading,
    subtitle,
    headingFontPx,
    subtitleFontPx,
  }: {
    context: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    style: StoreScreenshotTemplateStyle;
    sourceImage: HTMLImageElement;
    overlayImages: Array<{
      id?: string;
      image: HTMLImageElement;
      xRatio: number;
      yRatio: number;
      widthRatio: number;
      heightRatio: number;
      opacity?: number;
      borderRadiusRatio?: number;
    }>;
    heading: string;
    subtitle: string;
    headingFontPx: number;
    subtitleFontPx: number;
  }) {
    const layers = this.normalizeLayerStack(style.layerStack || []);
    const overlayById = new Map(
      overlayImages.map((item, index) => [item.id || `overlay_${index}`, item]),
    );
    let fallbackOverlayIndex = 0;

    layers.forEach((layer) => {
      if (!layer.visible) return;

      if (layer.kind === "background") {
        this.paintTemplateBackground(
          context,
          canvas.width,
          canvas.height,
          style,
        );
        return;
      }
      if (layer.kind === "main_image") {
        this.drawMainImageWithMask(context, canvas, sourceImage, style);
        return;
      }
      if (layer.kind === "overlay_image") {
        const overlay =
          (layer.refId && overlayById.get(layer.refId)) ||
          overlayImages[fallbackOverlayIndex];
        if (overlay) {
          this.drawOverlayImages(context, canvas, [overlay]);
          if (!layer.refId) fallbackOverlayIndex += 1;
        }
        return;
      }
      if (layer.kind === "text_heading") {
        this.drawCompositionTextLayer({
          context,
          canvas,
          text: heading,
          color: style.headingColor,
          fontStyle: style.headingFontStyle,
          fontWeight: style.headingWeight,
          fontFamily: style.headingFontFamily,
          fontSizePx: headingFontPx,
          minFontSizePx: Math.max(18, Math.round(headingFontPx * 0.42)),
          xRatio: style.headingXRatio,
          yRatio: style.headingYRatio,
          maxWidthRatio: style.headingMaxWidthRatio,
          textAlign: style.headingTextAlign,
          maxHeightRatio: 0.45,
          maxLines: 3,
          lineHeightMultiplier: 1.08,
        });
        return;
      }
      if (layer.kind === "text_subtitle" && style.showSubtitle) {
        this.drawCompositionTextLayer({
          context,
          canvas,
          text: subtitle,
          color: style.subtitleColor,
          fontStyle: style.subtitleFontStyle,
          fontWeight: style.subtitleWeight,
          fontFamily: style.subtitleFontFamily,
          fontSizePx: subtitleFontPx,
          minFontSizePx: Math.max(12, Math.round(subtitleFontPx * 0.52)),
          xRatio: style.subtitleXRatio,
          yRatio: style.subtitleYRatio,
          maxWidthRatio: style.subtitleMaxWidthRatio,
          textAlign: style.subtitleTextAlign,
          maxHeightRatio: 0.3,
          maxLines: 2,
          lineHeightMultiplier: 1.14,
        });
      }
    });
  }

  private drawMainImageWithMask(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    sourceImage: HTMLImageElement,
    style: StoreScreenshotTemplateStyle,
  ) {
    const inset = Math.max(
      0,
      Math.round(canvas.width * (style.mainImageMask?.insetRatio || 0)),
    );
    const baseWidth = Math.max(8, canvas.width - inset * 2);
    const baseHeight = Math.max(8, canvas.height - inset * 2);
    const centerX = Math.round(
      canvas.width * (style.mainImageTransform?.xRatio || 0.5),
    );
    const centerY = Math.round(
      canvas.height * (style.mainImageTransform?.yRatio || 0.5),
    );
    const scale = Math.max(0.2, style.mainImageTransform?.scale || 1);
    const width = Math.max(8, Math.round(baseWidth * scale));
    const height = Math.max(8, Math.round(baseHeight * scale));
    const x = Math.round(centerX - width / 2);
    const y = Math.round(centerY - height / 2);
    const rotationDeg = style.mainImageTransform?.rotationDeg || 0;

    context.save();
    context.globalAlpha = Math.max(
      0,
      Math.min(1, style.mainImageTransform?.opacity ?? 1),
    );
    context.translate(centerX, centerY);
    context.rotate((rotationDeg * Math.PI) / 180);
    context.translate(-centerX, -centerY);
    this.buildMainImageMaskPath(context, x, y, width, height, style);
    context.clip();
    const imageScale = Math.max(
      width / sourceImage.width,
      height / sourceImage.height,
    );
    const imageWidth = sourceImage.width * imageScale;
    const imageHeight = sourceImage.height * imageScale;
    const imageX = x + (width - imageWidth) / 2;
    const imageY = y + (height - imageHeight) / 2;
    context.drawImage(sourceImage, imageX, imageY, imageWidth, imageHeight);
    context.restore();
  }

  private buildMainImageMaskPath(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    style: StoreScreenshotTemplateStyle,
  ) {
    const maskType = style.mainImageMask?.type || "rounded_rect";
    const radiusRatio = Math.max(
      0,
      Math.min(0.5, style.mainImageMask?.radiusRatio ?? 0.08),
    );
    const radius = Math.round(Math.min(width, height) * radiusRatio);

    if (maskType === "circle") {
      context.beginPath();
      const r = Math.round(Math.min(width, height) / 2);
      context.arc(
        x + Math.round(width / 2),
        y + Math.round(height / 2),
        r,
        0,
        Math.PI * 2,
      );
      context.closePath();
      return;
    }

    if (maskType === "squircle") {
      const r = Math.max(10, radius);
      context.beginPath();
      context.moveTo(x + r, y);
      context.bezierCurveTo(
        x + width * 0.75,
        y,
        x + width,
        y + height * 0.08,
        x + width,
        y + r,
      );
      context.bezierCurveTo(
        x + width,
        y + height * 0.75,
        x + width * 0.92,
        y + height,
        x + width - r,
        y + height,
      );
      context.bezierCurveTo(
        x + width * 0.25,
        y + height,
        x,
        y + height * 0.92,
        x,
        y + height - r,
      );
      context.bezierCurveTo(
        x,
        y + height * 0.25,
        x + width * 0.08,
        y,
        x + r,
        y,
      );
      context.closePath();
      return;
    }

    if (maskType === "arch_wave_top") {
      const waveDepth = Math.max(
        0,
        Math.min(
          Math.round(height * 0.35),
          Math.round(height * (style.mainImageMask?.waveDepthRatio ?? 0.1)),
        ),
      );
      // Two-peak wave entirely within [y, y+height] — no off-canvas clipping
      context.beginPath();
      context.moveTo(x, y + waveDepth);
      context.quadraticCurveTo(
        x + Math.round(width * 0.25),
        y,
        x + Math.round(width * 0.5),
        y + Math.round(waveDepth * 0.5),
      );
      context.quadraticCurveTo(
        x + Math.round(width * 0.75),
        y,
        x + width,
        y + waveDepth,
      );
      context.lineTo(x + width, y + height - radius);
      context.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height,
      );
      context.lineTo(x + radius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - radius);
      context.closePath();
      return;
    }

    if (maskType === "arch_bottom") {
      const waveDepth = Math.max(
        0,
        Math.min(
          Math.round(height * 0.35),
          Math.round(height * (style.mainImageMask?.waveDepthRatio ?? 0.1)),
        ),
      );
      context.beginPath();
      context.moveTo(x, y + radius);
      context.quadraticCurveTo(x, y, x + radius, y);
      context.lineTo(x + width - radius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + radius);
      context.lineTo(x + width, y + height - waveDepth);
      context.quadraticCurveTo(
        x + Math.round(width / 2),
        y + height,
        x,
        y + height - waveDepth,
      );
      context.closePath();
      return;
    }

    if (maskType === "diamond") {
      context.beginPath();
      context.moveTo(x + Math.round(width / 2), y);
      context.lineTo(x + width, y + Math.round(height / 2));
      context.lineTo(x + Math.round(width / 2), y + height);
      context.lineTo(x, y + Math.round(height / 2));
      context.closePath();
      return;
    }

    if (maskType === "hexagon") {
      const qw = Math.round(width / 4);
      context.beginPath();
      context.moveTo(x + qw, y);
      context.lineTo(x + width - qw, y);
      context.lineTo(x + width, y + Math.round(height / 2));
      context.lineTo(x + width - qw, y + height);
      context.lineTo(x + qw, y + height);
      context.lineTo(x, y + Math.round(height / 2));
      context.closePath();
      return;
    }

    if (maskType === "shield") {
      const r = Math.max(4, radius);
      context.beginPath();
      context.moveTo(x + r, y);
      context.lineTo(x + width - r, y);
      context.quadraticCurveTo(x + width, y, x + width, y + r);
      context.lineTo(x + width, y + Math.round(height * 0.62));
      context.quadraticCurveTo(
        x + Math.round(width / 2),
        y + height,
        x,
        y + Math.round(height * 0.62),
      );
      context.lineTo(x, y + r);
      context.quadraticCurveTo(x, y, x + r, y);
      context.closePath();
      return;
    }

    if (maskType === "notched_rect") {
      const notch = Math.max(4, radius);
      context.beginPath();
      context.moveTo(x + notch, y);
      context.lineTo(x + width - notch, y);
      context.lineTo(x + width, y + notch);
      context.lineTo(x + width, y + height - notch);
      context.lineTo(x + width - notch, y + height);
      context.lineTo(x + notch, y + height);
      context.lineTo(x, y + height - notch);
      context.lineTo(x, y + notch);
      context.closePath();
      return;
    }

    this.drawRoundRect(context, x, y, width, height, radius);
  }

  private drawCompositionTextLayer({
    context,
    canvas,
    text,
    color,
    fontStyle,
    fontWeight,
    fontFamily,
    fontSizePx,
    minFontSizePx,
    xRatio,
    yRatio,
    maxWidthRatio,
    textAlign,
    maxHeightRatio,
    maxLines,
    lineHeightMultiplier,
  }: {
    context: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    text: string;
    color: string;
    fontStyle: "normal" | "italic";
    fontWeight: number;
    fontFamily: string;
    fontSizePx: number;
    minFontSizePx: number;
    xRatio: number;
    yRatio: number;
    maxWidthRatio: number;
    textAlign: "left" | "center" | "right";
    maxHeightRatio: number;
    maxLines: number;
    lineHeightMultiplier: number;
  }) {
    const x = Math.round(canvas.width * xRatio);
    const y = Math.round(canvas.height * yRatio);
    const maxWidth = Math.max(48, Math.round(canvas.width * maxWidthRatio));
    context.textBaseline = "top";
    context.fillStyle = color;
    context.textAlign = textAlign;
    const layout = this.fitWrappedTextBlock(context, {
      text,
      fontStyle,
      fontWeight,
      fontSizePx,
      minFontSizePx,
      fontFamily,
      maxWidth,
      maxHeight: Math.round(canvas.height * maxHeightRatio),
      maxLines,
      lineHeightMultiplier,
    });
    context.font = this.buildCanvasFont(
      fontStyle,
      fontWeight,
      layout.fontSizePx,
      fontFamily,
    );
    this.drawWrappedTextLines(context, layout.lines, x, y, layout.lineHeight);
  }

  private drawOverlayImages(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    overlays: Array<{
      id?: string;
      image: HTMLImageElement;
      xRatio: number;
      yRatio: number;
      widthRatio: number;
      heightRatio: number;
      opacity?: number;
      borderRadiusRatio?: number;
    }>,
  ) {
    overlays.forEach((overlay) => {
      const width = Math.max(8, Math.round(canvas.width * overlay.widthRatio));
      const height = Math.max(
        8,
        Math.round(canvas.height * overlay.heightRatio),
      );
      const centerX = Math.round(canvas.width * overlay.xRatio);
      const centerY = Math.round(canvas.height * overlay.yRatio);
      const x = centerX - Math.round(width / 2);
      const y = centerY - Math.round(height / 2);
      const radius = Math.max(
        0,
        Math.round(Math.min(width, height) * (overlay.borderRadiusRatio || 0)),
      );

      context.save();
      context.globalAlpha = Math.max(0, Math.min(1, overlay.opacity ?? 1));
      if (radius > 0) {
        this.drawRoundRect(context, x, y, width, height, radius);
        context.clip();
      }
      context.drawImage(overlay.image, x, y, width, height);
      context.restore();
    });
  }

  private drawAbsoluteTextLayer({
    context,
    canvas,
    style,
    heading,
    subtitle,
    headingFontPx,
    subtitleFontPx,
  }: {
    context: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    style: StoreScreenshotTemplateStyle;
    heading: string;
    subtitle: string;
    headingFontPx: number;
    subtitleFontPx: number;
  }) {
    const headingX = Math.round(canvas.width * style.headingXRatio);
    const headingY = Math.round(canvas.height * style.headingYRatio);
    const headingMaxWidth = Math.max(
      48,
      Math.round(canvas.width * style.headingMaxWidthRatio),
    );
    const subtitleX = Math.round(canvas.width * style.subtitleXRatio);
    const subtitleY = Math.round(canvas.height * style.subtitleYRatio);
    const subtitleMaxWidth = Math.max(
      48,
      Math.round(canvas.width * style.subtitleMaxWidthRatio),
    );

    context.textBaseline = "top";
    context.fillStyle = style.headingColor;
    context.textAlign = style.headingTextAlign;
    const headingLayout = this.fitWrappedTextBlock(context, {
      text: heading,
      fontStyle: style.headingFontStyle,
      fontWeight: style.headingWeight,
      fontSizePx: headingFontPx,
      minFontSizePx: Math.max(18, Math.round(headingFontPx * 0.42)),
      fontFamily: style.headingFontFamily,
      maxWidth: headingMaxWidth,
      maxHeight: Math.round(canvas.height * 0.45),
      maxLines: 3,
      lineHeightMultiplier: 1.08,
    });
    context.font = this.buildCanvasFont(
      style.headingFontStyle,
      style.headingWeight,
      headingLayout.fontSizePx,
      style.headingFontFamily,
    );
    this.drawWrappedTextLines(
      context,
      headingLayout.lines,
      headingX,
      headingY,
      headingLayout.lineHeight,
    );

    if (style.showSubtitle) {
      context.fillStyle = style.subtitleColor;
      context.textAlign = style.subtitleTextAlign;
      const subtitleLayout = this.fitWrappedTextBlock(context, {
        text: subtitle,
        fontStyle: style.subtitleFontStyle,
        fontWeight: style.subtitleWeight,
        fontSizePx: subtitleFontPx,
        minFontSizePx: Math.max(12, Math.round(subtitleFontPx * 0.52)),
        fontFamily: style.subtitleFontFamily,
        maxWidth: subtitleMaxWidth,
        maxHeight: Math.round(canvas.height * 0.3),
        maxLines: 2,
        lineHeightMultiplier: 1.14,
      });
      context.font = this.buildCanvasFont(
        style.subtitleFontStyle,
        style.subtitleWeight,
        subtitleLayout.fontSizePx,
        style.subtitleFontFamily,
      );
      this.drawWrappedTextLines(
        context,
        subtitleLayout.lines,
        subtitleX,
        subtitleY,
        subtitleLayout.lineHeight,
      );
    }
  }

  private paintTemplateBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    style: StoreScreenshotTemplateStyle,
  ) {
    if (style.backgroundType === "gradient") {
      this.paintGradientBackground(
        context,
        width,
        height,
        style.backgroundGradient,
      );
    } else {
      context.fillStyle = style.backgroundColor;
      context.fillRect(0, 0, width, height);
    }

    if (style.backgroundType === "pattern") {
      this.paintBackgroundPattern(
        context,
        width,
        height,
        style.backgroundPattern,
        style.patternColor,
        style.patternOpacity,
        style.patternScale,
        style.dotSizeScale,
        style.waveStyle,
      );
    }
  }

  private paintGradientBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    preset: StoreGradientPresetId,
  ) {
    const [from, to, accent] = this.getGradientPresetColors(preset);
    const gradient = context.createLinearGradient(0, 0, width, height);
    if (preset === "hot_pink_glow") {
      gradient.addColorStop(0, from);
      gradient.addColorStop(0.72, to);
      gradient.addColorStop(1, accent);
    } else {
      gradient.addColorStop(0, from);
      gradient.addColorStop(0.55, to);
      gradient.addColorStop(1, accent);
    }
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    const radial = context.createRadialGradient(
      Math.round(width * 0.18),
      Math.round(height * 0.14),
      Math.round(width * 0.04),
      Math.round(width * 0.18),
      Math.round(height * 0.14),
      Math.round(width * 0.58),
    );
    radial.addColorStop(0, "rgba(255,255,255,0.24)");
    radial.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = radial;
    context.fillRect(0, 0, width, height);
  }

  private getGradientPresetColors(
    preset: StoreGradientPresetId,
  ): [string, string, string] {
    switch (preset) {
      case "sunset_blush":
        return ["#FF9A9E", "#FAD0C4", "#FBC2EB"];
      case "hot_pink_glow":
        return ["#F24C9D", "#EE4FA8", "#F5C6CF"];
      case "ocean_depth":
        return ["#0F2027", "#203A43", "#2C5364"];
      case "mint_fresh":
        return ["#11998E", "#38EF7D", "#9EF7D4"];
      case "royal_plum":
        return ["#42275A", "#734B6D", "#B08ABF"];
      case "citrus_pop":
        return ["#F7971E", "#FFD200", "#FFF1A8"];
      case "midnight_steel":
        return ["#232526", "#414345", "#6D6F73"];
      case "sky_lagoon":
        return ["#00C6FF", "#0072FF", "#7FB9FF"];
      case "rose_gold":
        return ["#EFB5AE", "#C39EA0", "#B48A78"];
      case "emerald_glow":
        return ["#134E5E", "#71B280", "#A0D9A0"];
      case "twilight_berry":
        return ["#1D2B64", "#F8CDD0", "#D66D75"];
      case "amber_sky":
        return ["#F59E0B", "#F97316", "#FDE68A"];
      case "violet_dream":
        return ["#5B21B6", "#8B5CF6", "#C4B5FD"];
      case "coral_reef":
        return ["#FF6B6B", "#FF9A8B", "#FFD6A5"];
      case "arctic_mist":
        return ["#93C5FD", "#BFDBFE", "#E0F2FE"];
      case "lava_glow":
        return ["#7F1D1D", "#DC2626", "#F59E0B"];
      case "forest_canopy":
        return ["#14532D", "#16A34A", "#86EFAC"];
      case "peach_bloom":
        return ["#FB7185", "#FDBA74", "#FFE4E6"];
      case "neon_night":
        return ["#0B1020", "#312E81", "#22D3EE"];
      case "cobalt_sunrise":
        return ["#1D4ED8", "#2563EB", "#F59E0B"];
      case "lavender_haze":
        return ["#7C3AED", "#A78BFA", "#F5D0FE"];
      default:
        return ["#FF9A9E", "#FAD0C4", "#FBC2EB"];
    }
  }

  private paintBackgroundPattern(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    pattern: StoreBackgroundPattern,
    patternColor: string,
    patternOpacity: number,
    patternScale: number,
    dotSizeScale: number,
    waveStyle: WaveStyleVariant,
  ) {
    const opacity = Math.min(0.8, Math.max(0.12, patternOpacity));
    const scale = Math.min(1.8, Math.max(0.6, patternScale));
    const dots = Math.min(2.6, Math.max(0.6, dotSizeScale));
    const [r, g, b] = this.hexToRgb(patternColor);
    const light = `rgba(${Math.min(255, r + 28)}, ${Math.min(
      255,
      g + 28,
    )}, ${Math.min(255, b + 28)}, ${Math.min(0.85, opacity + 0.1)})`;
    const dark = `rgba(${Math.max(0, r - 22)}, ${Math.max(
      0,
      g - 22,
    )}, ${Math.max(0, b - 22)}, ${Math.max(0.18, opacity - 0.08)})`;

    if (pattern === "dot_grid") {
      const spacing = Math.max(18, Math.round(width * 0.02 * scale));
      const radius = Math.max(
        1.5,
        Math.round(width * 0.0022 * dots * Math.pow(scale, 0.35)),
      );
      context.fillStyle = dark;
      for (let y = Math.round(spacing * 0.5); y < height; y += spacing) {
        for (let x = Math.round(spacing * 0.5); x < width; x += spacing) {
          context.beginPath();
          context.arc(x, y, radius, 0, Math.PI * 2);
          context.fill();
        }
      }
      return;
    }

    if (pattern === "diagonal_lines") {
      const gap = Math.max(22, Math.round(width * 0.022 * scale));
      context.strokeStyle = light;
      context.lineWidth = Math.max(1.4, Math.round(width * 0.0022 * scale));
      for (let x = -height; x < width + height; x += gap) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x + height, height);
        context.stroke();
      }
      return;
    }

    if (pattern === "diamond_weave") {
      const gap = Math.max(26, Math.round(width * 0.026 * scale));
      const lineWidth = Math.max(1.2, Math.round(width * 0.0018 * scale));
      context.strokeStyle = dark;
      context.lineWidth = lineWidth;
      for (let x = -height; x < width + height; x += gap) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x + height, height);
        context.stroke();
      }
      context.strokeStyle = light;
      context.lineWidth = Math.max(1, lineWidth * 0.85);
      for (let x = 0; x < width + height; x += gap) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x - height, height);
        context.stroke();
      }
      return;
    }

    if (pattern === "aurora_glow") {
      const glowSpots = [
        { x: 0.2, y: 0.16, r: 0.38 },
        { x: 0.72, y: 0.26, r: 0.44 },
        { x: 0.46, y: 0.78, r: 0.52 },
      ];
      glowSpots.forEach((spot) => {
        const radius = Math.round(Math.min(width, height) * spot.r * scale);
        const gradient = context.createRadialGradient(
          Math.round(width * spot.x),
          Math.round(height * spot.y),
          Math.max(6, Math.round(radius * 0.05)),
          Math.round(width * spot.x),
          Math.round(height * spot.y),
          radius,
        );
        gradient.addColorStop(
          0,
          `rgba(${r}, ${g}, ${b}, ${Math.min(0.55, opacity + 0.1)})`,
        );
        gradient.addColorStop(
          0.42,
          `rgba(${Math.min(255, r + 18)}, ${Math.min(255, g + 18)}, ${Math.min(
            255,
            b + 18,
          )}, ${Math.max(0.14, opacity - 0.05)})`,
        );
        gradient.addColorStop(1, "rgba(255,255,255,0)");
        context.fillStyle = gradient;
        context.beginPath();
        context.arc(
          Math.round(width * spot.x),
          Math.round(height * spot.y),
          radius,
          0,
          Math.PI * 2,
        );
        context.fill();
      });
      return;
    }

    const waveVariant = this.getWaveVariantConfig(waveStyle);
    const waveGap = Math.max(
      42,
      Math.round(width * 0.042 * scale * waveVariant.gapMultiplier),
    );
    const waveHeight = Math.max(
      8,
      Math.round(height * 0.012 * waveVariant.heightMultiplier),
    );
    const waveWidth = Math.max(
      90,
      Math.round(width * 0.18 * waveVariant.widthMultiplier),
    );
    context.strokeStyle = light;
    context.lineWidth = Math.max(
      1.8,
      Math.round(width * 0.0028 * scale * waveVariant.lineMultiplier),
    );
    for (
      let y = Math.round(waveGap * 0.65);
      y < height + waveGap;
      y += waveGap
    ) {
      context.beginPath();
      context.moveTo(0, y);
      for (let x = 0; x <= width + waveWidth; x += waveWidth) {
        context.bezierCurveTo(
          x + waveWidth * 0.25,
          y - waveHeight,
          x + waveWidth * 0.75,
          y + waveHeight,
          x + waveWidth,
          y,
        );
      }
      context.stroke();
    }
  }

  private hexToRgb(hexColor: string): [number, number, number] {
    const clean = hexColor.replace("#", "").trim();
    const hex =
      clean.length === 3
        ? `${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`
        : clean;
    const valid = /^[0-9a-fA-F]{6}$/.test(hex) ? hex : "D0D5DD";
    const num = parseInt(valid, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  }

  private async generateScreenshotCopy({
    appName,
    appContext,
    targetPlatform,
    locale,
    sourceName,
    sourceImageDataUrl,
    useScreenshotContext = true,
    context,
    dimensions,
    model,
  }: {
    appName: string;
    appContext?: App | null;
    targetPlatform: ScreenshotTargetPlatform;
    locale: string;
    sourceName: string;
    sourceImageDataUrl?: string;
    useScreenshotContext?: boolean;
    context?: string;
    dimensions: DeviceDimension[];
    model?: string;
  }): Promise<{ heading: string; subtitle: string }> {
    const minWidth = Math.min(
      ...dimensions.map((dimension) => dimension.width),
    );
    const headingMaxChars = this.getHeadingMaxChars(minWidth);
    const headingMaxWords = SCREENSHOT_HEADING_MAX_WORDS;
    const subtitleMaxChars = this.getSubtitleMaxChars(minWidth);
    const subtitleMaxWords = SCREENSHOT_SUBTITLE_MAX_WORDS;
    const sourceHint = sourceName.replace(/\.[^/.]+$/, "");

    const schema = {
      type: "object",
      properties: {
        heading: { type: "string", maxLength: headingMaxChars },
        subtitle: { type: "string", maxLength: subtitleMaxChars },
      },
      required: ["heading", "subtitle"],
    };

    const prompt = [
      "Generate concise, high-converting app store screenshot copy.",
      ...buildAppContextLines(
        appContext || {
          name: appName,
        },
      ),
      `Target platform: ${targetPlatform}`,
      `Locale: ${locale}`,
      `Write heading and subtitle in locale language: ${locale}`,
      ...(useScreenshotContext
        ? [
            "Analyze the uploaded screenshot image and extract the most marketable user-visible value moment.",
            `Screenshot context hint: ${sourceHint}`,
            `Additional context (optional): ${context?.trim() || "N/A"}`,
          ]
        : [
            "Do not analyze screenshot visuals for this run.",
            `Optional context from user: ${context?.trim() || "N/A"}`,
          ]),
      `Heading max characters: ${headingMaxChars}`,
      `Heading max words: ${headingMaxWords}`,
      `Subtitle max characters: ${subtitleMaxChars}`,
      `Subtitle max words: ${subtitleMaxWords}`,
      "Constraints:",
      `- Heading must be ${headingMaxWords} words or fewer.`,
      "- Heading must be highly conversion-focused and reflect the screenshot/context value moment.",
      "- Subtitle must be a single short sentence that clarifies value quickly.",
      `- Subtitle must be ${subtitleMaxWords} words or fewer.`,
      "- Avoid quotation marks, emojis, hashtags, and trailing punctuation.",
      "- Copy should be conversion-oriented for app store screenshots.",
      "- Return natural title case.",
    ].join("\n");

    try {
      const generated = await AIService.generateJSON<{
        heading?: string;
        subtitle?: string;
      }>(prompt, {
        model: model || AIService.getDefaultModel(),
        temperature: 0.2,
        structuredOutputSchema: schema,
        structuredOutputName: "screenshot_copy",
        inputImages:
          useScreenshotContext && sourceImageDataUrl
            ? [{ dataUrl: sourceImageDataUrl }]
            : undefined,
      });

      const resolvedHeading = this.normalizeCopyText(
        generated?.heading || `${appName} in Seconds`,
      );
      const resolvedSubtitle = this.normalizeCopyText(
        generated?.subtitle || "Fast setup. Clear value.",
      );

      return {
        heading: resolvedHeading,
        subtitle: resolvedSubtitle,
      };
    } catch {
      return {
        heading: `${appName} Made Simple`,
        subtitle: "Fast workflow. Clear results.",
      };
    }
  }

  private getHeadingMaxChars(width: number): number {
    return Math.max(22, Math.min(42, Math.floor(width / 32)));
  }

  private getSubtitleMaxChars(width: number): number {
    return Math.max(36, Math.min(64, Math.floor(width / 20)));
  }

  private fitText(value: string, maxChars: number): string {
    const normalized = value.replace(/\s+/g, " ").trim();
    if (normalized.length <= maxChars) return normalized;
    return `${normalized.slice(0, Math.max(0, maxChars - 1)).trimEnd()}...`;
  }

  private normalizeCopyText(value: string): string {
    const normalized = String(value || "")
      .replace(/\s+/g, " ")
      .trim();
    return normalized;
  }

  private drawRoundRect(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) {
    const r = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
    context.beginPath();
    context.moveTo(x + r, y);
    context.arcTo(x + width, y, x + width, y + height, r);
    context.arcTo(x + width, y + height, x, y + height, r);
    context.arcTo(x, y + height, x, y, r);
    context.arcTo(x, y, x + width, y, r);
    context.closePath();
  }

  private fitWrappedTextBlock(
    context: CanvasRenderingContext2D,
    params: {
      text: string;
      fontStyle: StoreFontStyle;
      fontWeight: number;
      fontSizePx: number;
      minFontSizePx: number;
      fontFamily: string;
      maxWidth: number;
      maxHeight: number;
      maxLines: number;
      lineHeightMultiplier: number;
    },
  ) {
    const {
      text,
      fontStyle,
      fontWeight,
      fontSizePx,
      minFontSizePx,
      fontFamily,
      maxWidth,
      maxHeight,
      maxLines,
      lineHeightMultiplier,
    } = params;

    const normalizedText = String(text || "")
      .replace(/\s+/g, " ")
      .trim();
    const safeMaxWidth = Math.max(40, maxWidth);
    const safeMaxHeight = Math.max(20, maxHeight);

    let nextFontSize = Math.max(minFontSizePx, fontSizePx);
    let best = {
      lines: normalizedText ? [normalizedText] : [""],
      fontSizePx: nextFontSize,
      lineHeight: Math.round(nextFontSize * lineHeightMultiplier),
      height: Math.round(nextFontSize * lineHeightMultiplier),
    };

    while (nextFontSize >= minFontSizePx) {
      context.font = this.buildCanvasFont(
        fontStyle,
        fontWeight,
        nextFontSize,
        fontFamily,
      );
      const wrapped = this.wrapTextToWidth(
        context,
        normalizedText,
        safeMaxWidth,
      );
      const lineHeight = Math.max(
        nextFontSize,
        Math.round(nextFontSize * lineHeightMultiplier),
      );
      const height = Math.max(lineHeight, wrapped.length * lineHeight);
      best = {
        lines: wrapped,
        fontSizePx: nextFontSize,
        lineHeight,
        height,
      };
      if (
        height <= safeMaxHeight &&
        (wrapped.length <= maxLines || nextFontSize === minFontSizePx)
      ) {
        return best;
      }
      nextFontSize -= 2;
    }

    return best;
  }

  private wrapTextToWidth(
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): string[] {
    const normalized = String(text || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return [""];

    const words = normalized.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (context.measureText(candidate).width <= maxWidth || !currentLine) {
        currentLine = candidate;
        return;
      }

      lines.push(currentLine);
      if (context.measureText(word).width <= maxWidth) {
        currentLine = word;
        return;
      }

      const chunks = this.breakWordToWidth(context, word, maxWidth);
      lines.push(...chunks.slice(0, -1));
      currentLine = chunks[chunks.length - 1] || "";
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [normalized];
  }

  private breakWordToWidth(
    context: CanvasRenderingContext2D,
    word: string,
    maxWidth: number,
  ): string[] {
    const chars = Array.from(word);
    const segments: string[] = [];
    let current = "";

    chars.forEach((char) => {
      const candidate = `${current}${char}`;
      if (context.measureText(candidate).width <= maxWidth || !current) {
        current = candidate;
      } else {
        segments.push(current);
        current = char;
      }
    });

    if (current) {
      segments.push(current);
    }

    return segments.length > 0 ? segments : [word];
  }

  private truncateLinesToFit(
    context: CanvasRenderingContext2D,
    lines: string[],
    maxWidth: number,
    maxLines: number,
  ): string[] {
    if (lines.length <= maxLines) return lines;
    const sliced = lines.slice(0, maxLines);
    const lastIndex = sliced.length - 1;
    sliced[lastIndex] = this.ellipsizeText(
      context,
      sliced[lastIndex],
      maxWidth,
    );
    return sliced;
  }

  private ellipsizeText(
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): string {
    const ellipsis = "...";
    if (context.measureText(text).width <= maxWidth) {
      return text;
    }

    let current = text.trim();
    while (current.length > 0) {
      const candidate = `${current}${ellipsis}`;
      if (context.measureText(candidate).width <= maxWidth) {
        return candidate;
      }
      current = current.slice(0, -1).trimEnd();
    }

    return ellipsis;
  }

  private drawWrappedTextLines(
    context: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    top: number,
    lineHeight: number,
  ) {
    lines.forEach((line, index) => {
      context.fillText(line, x, top + index * lineHeight);
    });
  }

  private async loadImage(dataUrl: string): Promise<HTMLImageElement> {
    const image = new Image();
    image.src = dataUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () =>
        reject(new Error("Failed to load uploaded screenshot image."));
    });
    return image;
  }

  private toSafeName(value: string) {
    return value.replace(/[^a-zA-Z0-9-_]/g, "_");
  }

  private async dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }

  private buildScreenshotFileName(
    screenshot: GeneratedStoreScreenshot,
    appName: string,
  ) {
    const safeAppName = this.toSafeName(appName);
    const safeLocale = this.toSafeName(screenshot.locale);
    const safeDimension = this.toSafeName(
      `${screenshot.dimension.name}-${screenshot.dimension.width}x${screenshot.dimension.height}`,
    );
    const safeSource = this.toSafeName(
      screenshot.sourceName.replace(/\.[^/.]+$/, ""),
    );
    return `${safeAppName}-${screenshot.platform}-${safeLocale}-${safeDimension}-${safeSource}.png`;
  }

  private buildStoredScreenshotDeviceFolderName(
    asset: Pick<
      StoredStoreScreenshotAsset,
      "platform" | "dimension" | "locale"
    >,
  ) {
    const deviceFolder = `${asset.platform.toUpperCase()} - ${
      asset.dimension.name
    } (${asset.dimension.width}x${asset.dimension.height})`;
    return `${deviceFolder}/${asset.locale || "default"}`;
  }

  private inferFileExtension(pathOrUrl: string): string {
    const cleaned = String(pathOrUrl || "").split("?")[0];
    const ext = cleaned.split(".").pop();
    return ext || "png";
  }

  private buildStyleDocId(userId: string, appId: string, templateId: string) {
    return `${encodeURIComponent(userId)}__${appId}__${templateId}`;
  }

  private getWaveVariantConfig(variant: WaveStyleVariant) {
    if (variant === "gentle") {
      return {
        widthMultiplier: 1.2,
        heightMultiplier: 0.72,
        gapMultiplier: 1.08,
        lineMultiplier: 0.88,
      };
    }
    if (variant === "dramatic") {
      return {
        widthMultiplier: 0.84,
        heightMultiplier: 1.35,
        gapMultiplier: 0.9,
        lineMultiplier: 1.2,
      };
    }
    return {
      widthMultiplier: 1,
      heightMultiplier: 1,
      gapMultiplier: 1,
      lineMultiplier: 1,
    };
  }

  private async ensureCanvasFontsLoaded(
    style: StoreScreenshotTemplateStyle,
    headingFontPx: number,
    subtitleFontPx: number,
  ) {
    if (typeof document === "undefined" || !document.fonts?.load) {
      return;
    }

    const headingPrimary = this.extractPrimaryFontFamily(
      style.headingFontFamily,
    );
    const subtitlePrimary = this.extractPrimaryFontFamily(
      style.subtitleFontFamily,
    );

    try {
      await Promise.all([
        document.fonts.load(
          this.buildCanvasFont(
            style.headingFontStyle,
            style.headingWeight,
            headingFontPx,
            headingPrimary,
          ),
        ),
        document.fonts.load(
          this.buildCanvasFont(
            style.subtitleFontStyle,
            style.subtitleWeight,
            subtitleFontPx,
            subtitlePrimary,
          ),
        ),
      ]);
    } catch {
      // Ignore font loading failures and allow browser fallback fonts.
    }
  }

  private extractPrimaryFontFamily(fontFamily: string): string {
    const [primary = "Inter"] = fontFamily.split(",");
    return primary.trim();
  }

  private buildCanvasFont(
    fontStyle: StoreFontStyle,
    fontWeight: number,
    fontSizePx: number,
    fontFamily: string,
  ): string {
    const safeStyle = fontStyle === "italic" ? "italic" : "normal";
    return `${safeStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;
  }

  private getDimensionFormFactor(
    dimension: DeviceDimension,
  ): "phone" | "tablet" {
    const normalizedName = String(dimension?.name || "").toLowerCase();
    const normalizedCategory = String(
      (dimension as any)?.category || "",
    ).toLowerCase();

    if (
      normalizedCategory.includes("tablet") ||
      normalizedName.includes("tablet") ||
      normalizedName.includes('7"') ||
      normalizedName.includes('10"') ||
      normalizedName.includes("ipad")
    ) {
      return "tablet";
    }

    return "phone";
  }

  private resolveFrameProfile(
    dimension: DeviceDimension,
    androidDeviceFrameId: AndroidDeviceFrameId,
    previewDeviceId?: StorePreviewDeviceId,
  ) {
    const formFactor = this.getDimensionFormFactor(dimension);
    const defaultProfile: DeviceRenderProfile =
      dimension.platform === "ios"
        ? formFactor === "tablet"
          ? DEVICE_RENDER_PROFILES.ipad_pro_12_9
          : DEVICE_RENDER_PROFILES.iphone_15_pro_max
        : formFactor === "tablet"
          ? DEVICE_RENDER_PROFILES.android_tablet_10
          : DEVICE_RENDER_PROFILES[androidDeviceFrameId] ||
            DEVICE_RENDER_PROFILES.pixel_9_pro;

    if (!previewDeviceId) {
      return defaultProfile;
    }

    const selectedPreview = STORE_DEVICE_FRAME_PROFILES.find(
      (item) => item.id === previewDeviceId,
    );
    if (
      !selectedPreview ||
      selectedPreview.platform !== dimension.platform ||
      selectedPreview.formFactor !== formFactor
    ) {
      return defaultProfile;
    }

    return DEVICE_RENDER_PROFILES[previewDeviceId] || defaultProfile;
  }

  private resolveFrameBounds(
    canvas: HTMLCanvasElement,
    style: StoreScreenshotTemplateStyle,
    frameProfile: DeviceRenderProfile,
  ) {
    const maxFrameWidth = canvas.width * style.frameWidthRatio;
    const maxFrameHeight = canvas.height * style.frameHeightRatio;
    const aspectRatio = frameProfile.deviceWidth / frameProfile.deviceHeight;

    let width = Math.round(maxFrameWidth);
    let height = Math.round(width / aspectRatio);

    if (height > maxFrameHeight) {
      height = Math.round(maxFrameHeight);
      width = Math.round(height * aspectRatio);
    }

    return {
      width: Math.min(canvas.width, width),
      height: Math.min(canvas.height, height),
    };
  }

  async getScreenshotJobsByAppId(
    appId: string,
    userId: string,
  ): Promise<ScreenshotJobRecord[]> {
    try {
      const jobsRef = collection(db, STORE_SCREENSHOT_JOBS_COLLECTION);
      const q = query(
        jobsRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: data.id,
          appId: data.appId,
          appName: data.appName,
          templateId: data.templateId,
          templateName: data.templateName,
          targetPlatform: data.targetPlatform,
          locales: data.locales,
          dimensionLabels: data.dimensionLabels,
          sourceCount: data.sourceCount,
          expectedOutputCount: data.expectedOutputCount,
          generatedCount: data.generatedCount,
          status: data.status,
          progress: data.progress,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          error: data.error,
          savedVersionId: data.savedVersionId,
          savedVersionNumber:
            typeof data.savedVersionNumber === "number"
              ? data.savedVersionNumber
              : data.savedVersionNumber
                ? Number(data.savedVersionNumber)
                : undefined,
        } as ScreenshotJobRecord;
      });
    } catch (error) {
      console.error("Failed to load legacy screenshot jobs:", error);
      return [];
    }
  }

  async getScreenshotJob(jobId: string): Promise<ScreenshotJobRecord | null> {
    const jobRef = doc(db, STORE_SCREENSHOT_JOBS_COLLECTION, jobId);
    const snap = await getDoc(jobRef);
    if (!snap.exists()) {
      return null;
    }

    const data = snap.data();
    return {
      id: data.id || snap.id,
      appId: data.appId,
      appName: data.appName,
      templateId: data.templateId,
      templateName: data.templateName,
      targetPlatform: data.targetPlatform,
      locales: Array.isArray(data.locales) ? data.locales : [],
      dimensionLabels: Array.isArray(data.dimensionLabels)
        ? data.dimensionLabels
        : [],
      sourceCount: Number(data.sourceCount || 0),
      expectedOutputCount: Number(data.expectedOutputCount || 0),
      generatedCount: Number(data.generatedCount || 0),
      status: data.status,
      progress: data.progress || null,
      createdAt: this.convertTimestampToDate(data.createdAt).toISOString(),
      updatedAt: this.convertTimestampToDate(data.updatedAt).toISOString(),
      error: data.error,
      savedVersionId: data.savedVersionId,
      savedVersionNumber:
        typeof data.savedVersionNumber === "number"
          ? data.savedVersionNumber
          : data.savedVersionNumber
            ? Number(data.savedVersionNumber)
            : undefined,
      zipDownloadUrl: data.zipDownloadUrl || "",
      zipFileName: data.zipFileName || "",
      zipCreatedAt: data.zipCreatedAt
        ? this.convertTimestampToDate(data.zipCreatedAt).toISOString()
        : "",
    } as ScreenshotJobRecord;
  }

  async generateScreenshotsCloud(params: {
    userId: string;
    jobId?: string;
    appId: string;
    appName: string;
    templateId: string;
    templateName: string;
    targetPlatform: ScreenshotTargetPlatform;
    locales: string[];
    dimensions: DeviceDimension[];
    sourceImages: Array<{
      sourceName: string;
      sourceImageUrl: string;
      heading: string;
      subtitle: string;
      slotId?: string;
      slotName?: string;
      slotOrder?: number;
      slotStyle?: StoreScreenshotTemplateStyle;
      overlayImages?: Array<{
        id?: string;
        sourceImageUrl: string;
        xRatio: number;
        yRatio: number;
        widthRatio: number;
        heightRatio: number;
        opacity?: number;
        borderRadiusRatio?: number;
      }>;
    }>;
    template: StoreScreenshotTemplate;
    onProgress?: (progress: StoreScreenshotGenerationProgress) => void;
  }): Promise<{
    jobId: string;
  }> {
    type GenerateStoreScreenshotsCallableResult = {
      screenshots: Array<{
        locale: string;
        dimension: string;
        downloadUrl: string;
        sourceName: string;
      }>;
    };

    const {
      appId,
      appName,
      templateId,
      templateName,
      targetPlatform,
      locales,
      dimensions,
      sourceImages,
      template,
      onProgress,
    } = params;

    await auth.authStateReady();
    const currentUser = auth.currentUser;
    const resolvedUserId = currentUser?.email || currentUser?.uid || "";
    if (!resolvedUserId) {
      throw new Error("Authentication required. Please sign in.");
    }

    // Create job record
    const jobId =
      params.jobId ||
      `ss_job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const jobRef = doc(db, STORE_SCREENSHOT_JOBS_COLLECTION, jobId);
    const normalizedSourceImages = sourceImages.filter(
      (item) =>
        typeof item?.sourceImageUrl === "string" &&
        item.sourceImageUrl.trim().length > 0,
    );
    normalizedSourceImages.sort((left, right) => {
      const leftOrder =
        typeof left.slotOrder === "number"
          ? left.slotOrder
          : Number.MAX_SAFE_INTEGER;
      const rightOrder =
        typeof right.slotOrder === "number"
          ? right.slotOrder
          : Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return String(left.sourceName || "").localeCompare(
        String(right.sourceName || ""),
      );
    });
    if (normalizedSourceImages.length === 0) {
      throw new Error("Upload at least one screenshot image.");
    }

    const expectedOutputCount =
      locales.length * dimensions.length * normalizedSourceImages.length;

    await setDoc(jobRef, {
      id: jobId,
      appId,
      appName,
      userId: resolvedUserId,
      templateId,
      templateName,
      targetPlatform,
      locales,
      dimensionLabels: dimensions.map(
        (d) =>
          `${d.platform.toUpperCase()} - ${d.name} (${d.width}x${d.height})`,
      ),
      sourceCount: normalizedSourceImages.length,
      expectedOutputCount,
      generatedCount: 0,
      status: "queued",
      progress: {
        stage: "queued",
        message: "Preparing cloud generation",
        completed: 0,
        total: expectedOutputCount,
        percent: 0,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    this.postAuthenticatedJson<GenerateStoreScreenshotsCallableResult>(
      GENERATE_STORE_SCREENSHOTS_ENDPOINT,
      {
        appId,
        userId: resolvedUserId,
        template,
        dimensions,
        locales,
        sourceImages: normalizedSourceImages.map((item) => ({
          ...item,
          sourceName: String(item.sourceName || "").trim(),
          sourceImageUrl: String(item.sourceImageUrl || "").trim(),
          heading: String(item.heading || "").trim(),
          subtitle: String(item.subtitle || "").trim(),
          slotId: item.slotId ? String(item.slotId) : undefined,
          slotName: item.slotName ? String(item.slotName) : undefined,
          slotOrder:
            typeof item.slotOrder === "number" ? item.slotOrder : undefined,
          slotStyle: item.slotStyle,
          overlayImages: Array.isArray(item.overlayImages)
            ? item.overlayImages
                .filter((overlay) => overlay?.sourceImageUrl)
                .map((overlay) => ({
                  id: typeof overlay.id === "string" ? overlay.id : undefined,
                  sourceImageUrl: String(overlay.sourceImageUrl || ""),
                  xRatio: Number(overlay.xRatio) || 0.5,
                  yRatio: Number(overlay.yRatio) || 0.5,
                  widthRatio: Number(overlay.widthRatio) || 0.4,
                  heightRatio: Number(overlay.heightRatio) || 0.4,
                  opacity:
                    typeof overlay.opacity === "number" ? overlay.opacity : 1,
                  borderRadiusRatio:
                    typeof overlay.borderRadiusRatio === "number"
                      ? overlay.borderRadiusRatio
                      : 0,
                }))
            : [],
        })),
        jobId,
      },
    )
      .then(async (result) => {
        // On success, update job with results
        const screenshots = Array.isArray(result?.screenshots)
          ? result.screenshots
          : [];
        const generated: GeneratedStoreScreenshot[] = screenshots
          .map((s) => {
            const matchedDimension = dimensions.find(
              (d) => d.name === s.dimension,
            );
            if (!matchedDimension) {
              return null;
            }

            return {
              id: `${s.locale}_${s.dimension}_${s.sourceName || "source"}`,
              appId,
              appName,
              locale: s.locale,
              platform: matchedDimension.platform,
              dimension: matchedDimension,
              sourceName: s.sourceName || "cloud_generated",
              heading:
                normalizedSourceImages.find(
                  (item) => item.sourceName === s.sourceName,
                )?.heading || "",
              subtitle:
                normalizedSourceImages.find(
                  (item) => item.sourceName === s.sourceName,
                )?.subtitle || "",
              dataUrl: s.downloadUrl,
              createdAt: new Date().toISOString(),
            };
          })
          .filter((item): item is GeneratedStoreScreenshot => Boolean(item));

        await updateDoc(jobRef, {
          status: "processing",
          generatedCount: screenshots.length,
          progress: {
            stage: "saving",
            message: `Saving ${screenshots.length} cloud screenshots to the content library`,
            completed: screenshots.length,
            total: expectedOutputCount,
            percent:
              expectedOutputCount > 0
                ? Math.round((screenshots.length / expectedOutputCount) * 100)
                : 100,
          },
          updatedAt: new Date().toISOString(),
        });

        // Persist to versions
        const persisted = await this.persistGeneratedScreenshots({
          userId: resolvedUserId,
          appId,
          templateId,
          templateName,
          screenshots: generated,
          onProgress,
        });

        await updateDoc(jobRef, {
          status: "completed",
          generatedCount: persisted.assets.length,
          savedVersionId: persisted.version.versionId,
          savedVersionNumber: persisted.version.versionNumber,
          progress: {
            stage: "complete",
            message: `Saved version v${persisted.version.versionNumber} to the content library`,
            completed: persisted.assets.length,
            total: expectedOutputCount,
            percent: 100,
          },
          updatedAt: new Date().toISOString(),
        });
      })
      .catch(async (error) => {
        await updateDoc(jobRef, {
          status: "failed",
          error: error.message,
          progress: {
            stage: "failed",
            message: error.message,
            completed: 0,
            total: expectedOutputCount,
            percent: 0,
          },
          updatedAt: new Date().toISOString(),
        });
      });

    return { jobId };
  }
}

type DeviceRenderProfile = {
  platform: "ios" | "android";
  formFactor: "phone" | "tablet";
  deviceWidth: number;
  deviceHeight: number;
  frameRadiusRatio: number;
  bezelRatio: number;
  topInsetMultiplier: number;
  heightInsetMultiplier: number;
  screenRadiusMultiplier: number;
  cameraRadiusRatio: number;
  cameraTopRatio: number;
  cameraXRatio: number;
  screenCutout: "dynamic_island" | "punch_hole" | "camera_dot" | "none";
  shellPrimary: string;
  shellSecondary: string;
  shellTertiary: string;
};

const DEVICE_RENDER_PROFILES: Record<
  StorePreviewDeviceId,
  DeviceRenderProfile
> = {
  iphone_15_pro_max: {
    platform: "ios",
    formFactor: "phone",
    deviceWidth: 1290,
    deviceHeight: 2796,
    frameRadiusRatio: 0.14,
    bezelRatio: 0.02,
    topInsetMultiplier: 2.15,
    heightInsetMultiplier: 3.8,
    screenRadiusMultiplier: 0.88,
    cameraRadiusRatio: 0.018,
    cameraTopRatio: 0.04,
    cameraXRatio: 0.5,
    screenCutout: "dynamic_island",
    shellPrimary: "#181B20",
    shellSecondary: "#050608",
    shellTertiary: "#23272E",
  },
  ipad_pro_12_9: {
    platform: "ios",
    formFactor: "tablet",
    deviceWidth: 2048,
    deviceHeight: 2732,
    frameRadiusRatio: 0.052,
    bezelRatio: 0.016,
    topInsetMultiplier: 1.34,
    heightInsetMultiplier: 2.68,
    screenRadiusMultiplier: 0.42,
    cameraRadiusRatio: 0.006,
    cameraTopRatio: 0.026,
    cameraXRatio: 0.5,
    screenCutout: "none",
    shellPrimary: "#14161B",
    shellSecondary: "#050608",
    shellTertiary: "#1A1D22",
  },
  pixel_9_pro: {
    platform: "android",
    formFactor: "phone",
    deviceWidth: 720,
    deviceHeight: 1600,
    frameRadiusRatio: 0.112,
    bezelRatio: 0.024,
    topInsetMultiplier: 1.72,
    heightInsetMultiplier: 2.98,
    screenRadiusMultiplier: 0.78,
    cameraRadiusRatio: 0.016,
    cameraTopRatio: 0.044,
    cameraXRatio: 0.5,
    screenCutout: "punch_hole",
    shellPrimary: "#20262E",
    shellSecondary: "#11161D",
    shellTertiary: "#2A303A",
  },
  galaxy_s25_ultra: {
    platform: "android",
    formFactor: "phone",
    deviceWidth: 720,
    deviceHeight: 1560,
    frameRadiusRatio: 0.072,
    bezelRatio: 0.021,
    topInsetMultiplier: 1.6,
    heightInsetMultiplier: 2.84,
    screenRadiusMultiplier: 0.54,
    cameraRadiusRatio: 0.015,
    cameraTopRatio: 0.043,
    cameraXRatio: 0.5,
    screenCutout: "punch_hole",
    shellPrimary: "#1B2027",
    shellSecondary: "#0C1016",
    shellTertiary: "#262D36",
  },
  oneplus_13: {
    platform: "android",
    formFactor: "phone",
    deviceWidth: 720,
    deviceHeight: 1584,
    frameRadiusRatio: 0.096,
    bezelRatio: 0.023,
    topInsetMultiplier: 1.67,
    heightInsetMultiplier: 2.92,
    screenRadiusMultiplier: 0.72,
    cameraRadiusRatio: 0.016,
    cameraTopRatio: 0.044,
    cameraXRatio: 0.13,
    screenCutout: "punch_hole",
    shellPrimary: "#1A2128",
    shellSecondary: "#0F141B",
    shellTertiary: "#2B3440",
  },
  xiaomi_15_ultra: {
    platform: "android",
    formFactor: "phone",
    deviceWidth: 720,
    deviceHeight: 1600,
    frameRadiusRatio: 0.104,
    bezelRatio: 0.025,
    topInsetMultiplier: 1.74,
    heightInsetMultiplier: 3.02,
    screenRadiusMultiplier: 0.75,
    cameraRadiusRatio: 0.017,
    cameraTopRatio: 0.045,
    cameraXRatio: 0.5,
    screenCutout: "punch_hole",
    shellPrimary: "#2A2F35",
    shellSecondary: "#171C21",
    shellTertiary: "#353C45",
  },
  android_tablet_10: {
    platform: "android",
    formFactor: "tablet",
    deviceWidth: 1600,
    deviceHeight: 2560,
    frameRadiusRatio: 0.048,
    bezelRatio: 0.022,
    topInsetMultiplier: 1.42,
    heightInsetMultiplier: 2.88,
    screenRadiusMultiplier: 0.36,
    cameraRadiusRatio: 0.006,
    cameraTopRatio: 0.028,
    cameraXRatio: 0.5,
    screenCutout: "camera_dot",
    shellPrimary: "#1B2128",
    shellSecondary: "#10151C",
    shellTertiary: "#2C343D",
  },
};

const instance = new ScreenshotService();
export default instance;
